import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../../utils/logger';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  useTheme,
  useMediaQuery,
  Autocomplete,
  InputAdornment,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { hasPermissionFromToken } from '../../utils/rolePermissions';
import ModernTemplateBuilder from '../../components/TemplateBuilder/ModernTemplateBuilder';
import AITemplateGeneratorDialog, { AIGeneratedTemplate } from '../../components/TemplateBuilder/AITemplateGeneratorDialog';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { lato, primaryButtonSx, sectionLabelSx, sectionTitleSx } from '../../styles/adminPageStyles';
import { locationLabel } from '../../utils/terminology';
import { resolveCheckSingularLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

type QuestionType = 'text' | 'multiple_choice' | 'yes_no' | 'yes_no_na' | 'number' | 'date' | 'signature' | 'text_header' | 'email_check' | 'image_type';

interface Question {
  question_id?: string;
  question_text: string;
  question_type: QuestionType;
  is_required: boolean;
  image_required?: boolean;
  force_image_on_yes?: boolean;
  force_image_on_no?: boolean;
  hide_on_return_if_yes?: boolean;
  hide_on_return_if_no?: boolean;
  hide_on_return_if_na?: boolean;
  show_conditions?: any;
  options?: string[];
  tags?: string[];
  allow_multiple_selections?: boolean;
  min_selections?: number;
  max_selections?: number;
}

interface AssetType {
  asset_type_id: string;
  type_name: string;
}

interface CheckTemplate {
  template_id: string;
  template_name: string;
  description: string;
  category: string;
  is_active: boolean;
  is_global?: boolean;
  created_by_owner?: boolean;
  force_condition_images?: boolean;
  min_condition_images?: number;
  allow_email_completion?: boolean;
  allow_skip_dvla?: boolean;
  tags: string[];
  asset_types: AssetType[];
  questions: Question[];
  question_count: number;
  created_at: string;
  updated_at: string;
}



interface CreateAssetTypeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (newAssetType: { asset_type_id: string; type_name: string }) => void;
}

const CreateAssetTypeDialog: React.FC<CreateAssetTypeDialogProps> = ({ open, onClose, onCreated }) => {
  const [typeName, setTypeName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!typeName.trim()) {
      setError('Asset type name is required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${API_BASE_URL}/asset-types`,
        { 
          type_name: typeName,
          description: description 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      logger.debug('Asset type creation response:', response.data);
      
      // Extract the asset type data however we can
      let assetTypeId: string | null = null;
      let typeNameValue: string = typeName; // Default to input value
      
      // Try to extract from response data
      const assetTypeData = response.data.data || response.data;
      
      if (assetTypeData) {
        // Try multiple possible property names for id
        assetTypeId = 
          assetTypeData.asset_type_id || 
          assetTypeData.id || 
          assetTypeData.assetTypeId || 
          assetTypeData.insertId?.toString();
          
        // Try to get the type name from response, but fall back to input value
        const responseTypeName = 
          assetTypeData.type_name || 
          assetTypeData.name || 
          assetTypeData.typeName;
          
        if (responseTypeName) {
          typeNameValue = responseTypeName;
        }
      }
      
      // If we don't have an ID from the response, generate a temporary one
      if (!assetTypeId) {
        assetTypeId = `temp_${Date.now()}`;
        logger.debug('Using temporary ID for new asset type:', assetTypeId);
      }
      
      // Always create a formatted asset type, even with fallback values
      const formattedAssetType = {
        asset_type_id: assetTypeId,
        type_name: typeNameValue
      };
      
      logger.debug('Formatted asset type:', formattedAssetType);
      
      // Call onCreated with the formatted asset type data
      onCreated(formattedAssetType);
      
      // Reset form
      setTypeName('');
      setDescription('');
      
      // Close dialog to improve UX
      onClose();
    } catch (error) {
      console.error('Error creating asset type:', error);
      // Show a more specific error message if available
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to create asset type. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Clear form when dialog closes
      setTypeName('');
      setDescription('');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Asset Type</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Asset Type Name"
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitting ? 'Creating...' : 'Create Asset Type'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CHECK_TYPE_TAGS = ['safety', 'loan', 'delivery', 'location'] as const;

const finalizeTemplateTags = (
  tags: string[],
  perms: { allowLocationChecks: boolean; allowLoanChecks: boolean; allowDeliveryChecks: boolean },
  fallbackTag?: string | null,
): string[] => {
  let sanitized = tags.filter((t) => {
    if (t === 'location' && !perms.allowLocationChecks) return false;
    if (t === 'loan' && !perms.allowLoanChecks) return false;
    if (t === 'delivery' && !perms.allowDeliveryChecks) return false;
    return true;
  });
  if (!sanitized.some((t) => CHECK_TYPE_TAGS.includes(t as typeof CHECK_TYPE_TAGS[number]))) {
    if (fallbackTag === 'location' && perms.allowLocationChecks) sanitized = ['location'];
    else if (fallbackTag === 'loan' && perms.allowLoanChecks) sanitized = ['loan'];
    else if (fallbackTag === 'delivery' && perms.allowDeliveryChecks) sanitized = ['delivery'];
    else sanitized = ['safety'];
  }
  return sanitized;
};

const CheckTemplateManagement: React.FC<{ filterTag?: string | null }> = ({ filterTag }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { adminUiLabels } = useAdminUiLabels();
  const [templates, setTemplates] = useState<CheckTemplate[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CheckTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [newQuestion, setNewQuestion] = useState<Question>({
    question_text: '',
    question_type: 'yes_no_na',
    is_required: true,
    image_required: false,
    force_image_on_yes: false,
    force_image_on_no: false,
    hide_on_return_if_yes: false,
    hide_on_return_if_no: false,
    hide_on_return_if_na: false,
    allow_multiple_selections: false,
    min_selections: undefined,
    max_selections: undefined,
  });
  const [selectedAssetTypeIds, setSelectedAssetTypeIds] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState<number | null>(null);
  const [editQuestionDialogOpen, setEditQuestionDialogOpen] = useState(false);
  const [currentEditQuestion, setCurrentEditQuestion] = useState<Question | null>(null);
  const [createAssetTypeDialogOpen, setCreateAssetTypeDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    detail?: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [businessPermissions, setBusinessPermissions] = useState<{
    allowTemplateCreation: boolean;
    allowSafetyChecks: boolean;
    allowLoanChecks: boolean;
    allowDeliveryChecks: boolean;
    allowLocationChecks: boolean;
    useSiteTerminology: boolean;
    allowFrequencyScheduling: boolean;
    allowAiTemplateGeneration: boolean;
  }>({
    allowTemplateCreation: true, // Default to true, will be updated after fetching
    allowSafetyChecks: true,
    allowLoanChecks: true,
    allowDeliveryChecks: true,
    allowLocationChecks: false,
    useSiteTerminology: false,
    allowFrequencyScheduling: false,
    allowAiTemplateGeneration: false,
  });
  const [canCreateGlobal, setCanCreateGlobal] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const [canManageTemplates, setCanManageTemplates] = useState(false);
  const [forceConditionImages, setForceConditionImages] = useState(false);
  const [minConditionImages, setMinConditionImages] = useState(0);
  const [allowSkipDvla, setAllowSkipDvla] = useState(false);
  const [allowEmailCompletion, setAllowEmailCompletion] = useState(false);
  const [requiresSection68Waiver, setRequiresSection68Waiver] = useState(false);
  const [section68WaiverText, setSection68WaiverText] = useState<string>('');
  const [requiresWholeGoodNumber, setRequiresWholeGoodNumber] = useState(false);
  const [wholeGoodNumberPrefix, setWholeGoodNumberPrefix] = useState('');
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [sendEmailTemplate, setSendEmailTemplate] = useState<CheckTemplate | null>(null);
  const [sendEmailRecipientEmail, setSendEmailRecipientEmail] = useState('');
  const [sendEmailRecipientName, setSendEmailRecipientName] = useState('');
  const [sendEmailCustomMessage, setSendEmailCustomMessage] = useState('');
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [suggestedFrequencyEnabled, setSuggestedFrequencyEnabled] = useState(false);
  const [suggestedFrequencyByAssetType, setSuggestedFrequencyByAssetType] = useState<Record<string, { value: number; unit: string }>>({});
  const [draftAgreements, setDraftAgreements] = useState<any[]>([]);
  const [draftSchedules, setDraftSchedules] = useState<any[]>([]);

  // JSON Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [importParsed, setImportParsed] = useState<any | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importStep, setImportStep] = useState<'input' | 'preview' | 'done'>('input');

  // AI Template Generator state
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);

  // Get template permission on mount
  useEffect(() => {
    setCanManageTemplates(hasPermissionFromToken('manage_templates'));
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setError('Authentication error: No token found');
        return;
      }

      logger.debug('Fetching templates...');
      // Include archived templates in the API request
      const response = await axios.get(`${API_BASE_URL}/check-templates?includeArchived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.debug('Templates response:', response.data);

      let templatesData = [];
      if (response.data && Array.isArray(response.data)) {
        templatesData = response.data;
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        templatesData = response.data.data;
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected response format from server');
        setTemplates([]);
        return;
      }

      // Process templates to ensure tags are properly parsed
      const processedTemplates: CheckTemplate[] = templatesData.map((template: any) => {
        let parsedTags: string[] = [];
        if (typeof template.tags === 'string') {
          try {
            const raw = JSON.parse(template.tags);
            parsedTags = Array.isArray(raw) ? raw.map((t: any) => String(t)) : [];
          } catch (e) {
            console.warn('Failed to parse tags as JSON:', template.tags);
            parsedTags = template.tags ? [String(template.tags)] : [];
          }
        } else if (Array.isArray(template.tags)) {
          parsedTags = (template.tags as any[]).map((t: any) => String(t));
        } else {
          parsedTags = [];
        }
        return { ...template, tags: parsedTags } as CheckTemplate;
      });

      // Apply Safety vs Loan vs Delivery filtering when requested
      const filteredTemplates = (filterTag === undefined || filterTag === null)
        ? processedTemplates
        : processedTemplates.filter((t: CheckTemplate) => {
            const tags = Array.isArray(t.tags) ? t.tags : [];
            const tagLower = tags.map((tag: string) => String(tag).toLowerCase());
            
            // For safety filter: show templates with no tags OR explicitly tagged as 'safety'
            if (filterTag.toLowerCase() === 'safety') {
              return (tags.length === 0 || tagLower.includes('safety')) && !tagLower.includes('location');
            }
            
            // For loan/delivery: only show templates explicitly tagged
            return tagLower.includes(filterTag.toLowerCase());
          });

      logger.debug('Processed templates with parsed tags:', processedTemplates);
      logger.debug('Filtered templates (filterTag=%s):', filterTag, filteredTemplates);
      setTemplates(filteredTemplates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
        setError(`Failed to fetch templates: ${errorMessage}`);
        console.error('Error details:', {
          status: err.response?.status,
          data: err.response?.data,
          headers: err.response?.headers
        });
      } else {
        setError('Failed to fetch templates: Unknown error');
      }
    } finally {
      setLoading(false);
    }
  }, [filterTag]);

  useEffect(() => {
    logger.debug('CheckTemplateManagement component mounted');
    fetchTemplates();
    if (filterTag !== 'location') {
      fetchAssetTypes();
    }
    fetchBusinessPermissions();
    checkUserPermissions();
  }, [fetchTemplates, filterTag]);

  const extractOptionTexts = (options: any): string[] => {
    if (!Array.isArray(options)) return [];
    const seen = new Set<string>();
    return options
      .map((opt: any) => (typeof opt === 'string' ? opt : (opt && typeof opt.option_text === 'string' ? opt.option_text : '')))
      .filter((text: string) => Boolean(text && text.trim()))
      .map((text: string) => text.trim())
      .filter((text: string) => {
        const key = text.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  /** Deduplicate option strings for a question (preserve order, first occurrence wins) to prevent duplicate options on save. */
  const dedupeOptionStrings = (options: string[] | undefined): string[] => {
    if (!Array.isArray(options)) return [];
    const seen = new Set<string>();
    return options.filter((t) => {
      const key = String(t || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((t) => String(t).trim()).filter(Boolean);
  };

  // Helper function to check if current business can edit global templates
  const canEditGlobalTemplate = () => {
    const businessName = localStorage.getItem('businessName');
    // Only Demo Check Now can edit/delete global templates
    return businessName === 'Demo Check Now';
  };

  const checkUserPermissions = () => {
    // Allow all authenticated users to create global templates
    setCanCreateGlobal(true);
  };

  const fetchAssetTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setError('Authentication error: No token found');
        return;
      }

      logger.debug('Fetching asset types...');
      const response = await axios.get(`${API_BASE_URL}/asset-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.debug('Asset types response:', response.data);

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setAssetTypes(response.data.data);
      } else {
        console.error('Unexpected asset types response format:', response.data);
        setError('Failed to fetch asset types: Unexpected response format');
      }
    } catch (error) {
      console.error('Error fetching asset types:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
        setError(`Failed to fetch asset types: ${errorMessage}`);
      } else {
        setError('Failed to fetch asset types: Unknown error');
      }
    }
  };

  const fetchBusinessPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      
      if (!token || !businessId) {
        console.warn('No authentication token or business ID found');
        // Default to true when no business context is available for backwards compatibility
        setBusinessPermissions({
          allowTemplateCreation: true,
          allowSafetyChecks: true,
          allowLoanChecks: true,
          allowDeliveryChecks: true,
          allowLocationChecks: false,
          useSiteTerminology: false,
          allowFrequencyScheduling: false,
          allowAiTemplateGeneration: false,
        });
        return;
      }

      logger.debug('Fetching business permissions for business:', businessId);
      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.debug('Business permissions response:', response.data);
      
      const businessData = response.data;
      setBusinessPermissions({
        // Handle database values: 1 = true, 0 = false, null/undefined = true (default)
        allowTemplateCreation: businessData.allow_template_creation !== 0,
        allowSafetyChecks: businessData.allow_safety_checks !== 0,
        allowLoanChecks: businessData.allow_loan_checks !== 0,
        allowDeliveryChecks: businessData.allow_delivery_checks !== 0,
        allowLocationChecks: businessData.allow_location_checks === 1 || businessData.allow_location_checks === true,
        useSiteTerminology: businessData.use_site_terminology === 1 || businessData.use_site_terminology === true,
        allowFrequencyScheduling: businessData.allow_frequency_scheduling === 1 || businessData.allow_frequency_scheduling === true,
        // null/undefined means the column hasn't been set yet → default to false (opt-in feature)
        allowAiTemplateGeneration: businessData.allow_ai_template_generation === 1 || businessData.allow_ai_template_generation === true,
      });
    } catch (error) {
      console.error('Error fetching business permissions:', error);
      // Default to true on error for backwards compatibility
      setBusinessPermissions({
        allowTemplateCreation: true,
        allowSafetyChecks: true,
        allowLoanChecks: true,
        allowDeliveryChecks: true,
        allowLocationChecks: false,
        useSiteTerminology: false,
        allowFrequencyScheduling: false,
        allowAiTemplateGeneration: false,
      });
    }
  };

  const handleDuplicateTemplate = async (template: CheckTemplate) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/check-templates/${template.template_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data) {
        setError('Failed to fetch template details for duplication');
        return;
      }

      const t = response.data;
      setTemplateName('');
      setTemplateDescription(t.description || '');
      setTemplateTags([]);
      setSelectedAssetTypeIds([]);
      setIsGlobal(false);

      const normalizedQuestions: Question[] = (t.questions || [])
        .filter((q: any) => {
          const rawTags = Array.isArray(q.tags) ? q.tags : [];
          const tagSet = rawTags.map((x: any) => String(x).toLowerCase());
          return !(tagSet.includes('loan_screen_2') || tagSet.includes('loan_screen_3'));
        })
        .map((q: any) => ({
        question_id: q.question_id,
        question_text: q.question_text,
        question_type: q.question_type,
        is_required: q.required === 1 || q.required === true,
        image_required: q.image_required === 1 || q.image_required === true,
        force_image_on_yes: q.force_image_on_yes === 1 || q.force_image_on_yes === true,
        force_image_on_no: q.force_image_on_no === 1 || q.force_image_on_no === true,
        hide_on_return_if_yes: q.hide_on_return_if_yes === 1 || q.hide_on_return_if_yes === true,
        hide_on_return_if_no: q.hide_on_return_if_no === 1 || q.hide_on_return_if_no === true,
        hide_on_return_if_na: q.hide_on_return_if_na === 1 || q.hide_on_return_if_na === true,
        options: extractOptionTexts(q.options),
        tags: Array.isArray(q.tags) ? q.tags.map((x: any) => String(x)) : [],
        allow_multiple_selections: q.allow_multiple_selections === 1 || q.allow_multiple_selections === true,
        min_selections: q.min_selections || undefined,
        max_selections: q.max_selections || undefined,
      }));
      setQuestions(normalizedQuestions);

      setCreateDialogOpen(true);
      setEditDialogOpen(false);
      setSelectedTemplate(null);
      setNewQuestion({
        question_text: '',
        question_type: 'yes_no_na',
        is_required: true,
        image_required: false,
      });
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError('Failed to duplicate template');
    }
  };

  const resetForm = () => {
    setTemplateName('');
    setTemplateDescription('');
    // Default to safety check type based on filterTag context
    if (filterTag === 'loan') {
      setTemplateTags(['loan']);
    } else if (filterTag === 'delivery') {
      setTemplateTags(['delivery']);
    } else if (filterTag === 'location' && businessPermissions.allowLocationChecks) {
      setTemplateTags(['location']);
    } else {
      setTemplateTags(['safety']);
    }
    setSelectedAssetTypeIds([]);
    setQuestions([]);
    setIsGlobal(false);
    setForceConditionImages(false);
    setMinConditionImages(0);
    setAllowSkipDvla(false);
    setAllowEmailCompletion(false);
    setRequiresSection68Waiver(false);
    setSection68WaiverText('');
    setRequiresWholeGoodNumber(false);
    setWholeGoodNumberPrefix('');
    setSuggestedFrequencyEnabled(false);
    setSuggestedFrequencyByAssetType({});
    setDraftAgreements([]);
    setDraftSchedules([]);
    setNewQuestion({
      question_text: '',
      question_type: 'yes_no_na',
      is_required: true,
      image_required: false,
    });
    setEditQuestionIndex(null);
    setCurrentEditQuestion(null);
  };

  const handleApplyAiTemplate = (generated: AIGeneratedTemplate) => {
    resetForm();
    setTemplateName(generated.template_name);
    setTemplateDescription(generated.description || '');

    // Apply the suggested tag, respecting current filterTag and permissions
    const aiTag = generated.suggested_tags?.[0] || filterTag || 'safety';
    const validTag = finalizeTemplateTags(
      [aiTag],
      businessPermissions,
      filterTag,
    );
    setTemplateTags(validTag);

    // Map generated questions to the internal Question shape
    const mapped: Question[] = generated.questions.map((q) => ({
      question_text: q.question_text,
      question_type: q.question_type as QuestionType,
      is_required: q.is_required !== false,
      image_required: !!q.image_required,
      force_image_on_yes: !!q.force_image_on_yes,
      force_image_on_no: !!q.force_image_on_no,
      hide_on_return_if_yes: false,
      hide_on_return_if_no: false,
      hide_on_return_if_na: false,
      options: Array.isArray(q.options) ? q.options : [],
      allow_multiple_selections: !!q.allow_multiple_selections,
      min_selections: q.min_selections,
      max_selections: q.max_selections,
    }));
    setQuestions(mapped);
    setCreateDialogOpen(true);
  };

  const handleCreateTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      if (!templateName.trim()) {
        setError('Template name is required');
        return;
      }

      const finalTemplateTags = finalizeTemplateTags(templateTags || [], businessPermissions, filterTag);

      // Map questions to ensure required and image_required properties are properly formatted for backend
      // Deduplicate options per question to prevent duplicate options in templates
      const formattedQuestions = questions.map(question => ({
        ...question,
        required: question.is_required === true ? 1 : 0,
        image_required: question.image_required === true ? 1 : 0,
        allow_multiple_selections: question.allow_multiple_selections === true ? 1 : 0,
        min_selections: question.min_selections || null,
        max_selections: question.max_selections || null,
        options: dedupeOptionStrings(question.options),
      }));

      logger.debug('🔍 Debug - Original questions state:', questions);
      logger.debug('🔍 Debug - Formatted questions for backend:', formattedQuestions);
      logger.debug('🔍 Debug - Multi-select fields in first question:', formattedQuestions.length > 0 ? {
        allow_multiple_selections: formattedQuestions[0].allow_multiple_selections,
        min_selections: formattedQuestions[0].min_selections,
        max_selections: formattedQuestions[0].max_selections
      } : 'No questions');

      // Auto-add loan-specific questions if template is tagged with 'loan'
      let finalQuestions = formattedQuestions;
      if (finalTemplateTags.includes('loan')) {
        finalQuestions = addLoanSpecificQuestions(formattedQuestions);
      }

      logger.debug('🔍 Debug - Final questions being sent:', finalQuestions);
      logger.debug('🔍 Debug - Template payload:', {
        template_name: templateName,
        description: templateDescription,
        asset_type_ids: selectedAssetTypeIds,
        questions: finalQuestions,
        tags: finalTemplateTags,
      });

      const assetTypeIdsForSave = finalTemplateTags.includes('location') ? [] : selectedAssetTypeIds;
      const suggested_frequency = suggestedFrequencyEnabled
        ? suggestedFrequencyByAssetType
        : assetTypeIdsForSave.reduce((acc, id) => ({ ...acc, [id]: { value: null, unit: null } }), {} as Record<string, { value: null; unit: null }>);
      const response = await axios.post(`${API_BASE_URL}/check-templates`, {
        template_name: templateName,
        description: templateDescription,
        asset_type_ids: assetTypeIdsForSave,
        questions: finalQuestions,
        tags: finalTemplateTags,
        is_global: isGlobal,
        force_condition_images: forceConditionImages,
        min_condition_images: minConditionImages,
        allow_skip_dvla: finalTemplateTags.includes('location') ? false : allowSkipDvla,
        allow_email_completion: finalTemplateTags.includes('location') ? false : allowEmailCompletion,
        requires_section_6_8_waiver: finalTemplateTags.includes('delivery') ? requiresSection68Waiver : false,
        section_6_8_waiver_text: finalTemplateTags.includes('delivery') && requiresSection68Waiver
          ? (section68WaiverText && section68WaiverText.trim() ? section68WaiverText : null)
          : null,
        requires_whole_good_number: finalTemplateTags.includes('delivery') ? requiresWholeGoodNumber : false,
        whole_good_number_prefix: finalTemplateTags.includes('delivery') && requiresWholeGoodNumber
          ? (wholeGoodNumberPrefix.trim().toUpperCase() || 'WGS')
          : null,
        suggested_frequency,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.debug('🔍 Debug - Backend response:', response.data);

      // Check if the response is successful
      if (response.data) {
        // If the response has a success property, check it
        if (response.data.success !== false) {
          // Save draft user agreements if any exist
          const newTemplateId = response.data.templateId; // Backend returns { templateId: 'xxx' }
          logger.debug('🔍 DEBUG: draftAgreements:', draftAgreements);
          logger.debug('🔍 DEBUG: newTemplateId:', newTemplateId);
          logger.debug('🔍 DEBUG: draftAgreements.length:', draftAgreements.length);
          if (draftAgreements.length > 0 && newTemplateId) {
            try {
              logger.debug(`📝 Saving ${draftAgreements.length} draft user agreements for template ${newTemplateId}`);
              for (const agreement of draftAgreements) {
                if (agreement.agreement_type === 'pdf') {
                  // Handle PDF upload
                  const formDataUpload = new FormData();
                  formDataUpload.append('pdf', agreement.pdf_file);
                  formDataUpload.append('template_id', newTemplateId);
                  formDataUpload.append('agreement_title', agreement.agreement_title);
                  formDataUpload.append('order_number', String(agreement.order_number));
                  formDataUpload.append('is_required', String(agreement.is_required ? 1 : 0));
                  
                  await axios.post(
                    `${API_BASE_URL}/template-user-agreements/upload-pdf`,
                    formDataUpload,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                      },
                    }
                  );
                } else {
                  // Handle text agreements
                  await axios.post(
                    `${API_BASE_URL}/template-user-agreements`,
                    {
                      template_id: newTemplateId,
                      agreement_type: agreement.agreement_type,
                      agreement_title: agreement.agreement_title,
                      agreement_content: agreement.agreement_content,
                      order_number: agreement.order_number,
                      is_required: agreement.is_required ? 1 : 0,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                }
              }
              logger.debug('✅ All draft user agreements saved successfully');
            } catch (agreementError) {
              console.error('⚠️ Error saving user agreements:', agreementError);
              // Don't fail the whole operation, just warn
            }
          }

          // Save draft schedules if any exist
          logger.debug('🔍 DEBUG: draftSchedules:', draftSchedules);
          logger.debug('🔍 DEBUG: draftSchedules.length:', draftSchedules.length);
          if (draftSchedules.length > 0 && newTemplateId) {
            try {
              logger.debug(`📅 Saving ${draftSchedules.length} draft schedules for template ${newTemplateId}`);
              for (const schedule of draftSchedules) {
                const scheduleData = {
                  ...schedule,
                  template_id: newTemplateId
                };
                delete scheduleData.schedule_id; // Remove draft ID, let backend generate real one
                
                await axios.post(
                  `${API_BASE_URL}/template-check-schedules`,
                  scheduleData,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              }
              logger.debug('✅ All draft schedules saved successfully');
            } catch (scheduleError) {
              console.error('⚠️ Error saving schedules:', scheduleError);
              // Don't fail the whole operation, just warn
            }
          }
          
          // Refresh the templates list
          await fetchTemplates();
          setCreateDialogOpen(false);
          resetForm();
          setSnackbar({
            open: true,
            message: 'Template created successfully',
            severity: 'success'
          });
          return;
        }
      }

      // If we get here, something went wrong
      setError('Failed to create template. Please try again.');
    } catch (error) {
      console.error('Error creating template:', error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to create template. Please try again.');
      }
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const finalTemplateTags = finalizeTemplateTags(templateTags || [], businessPermissions, filterTag);

      // Map questions to ensure required and image_required properties are properly formatted for backend
      // Deduplicate options per question to prevent duplicate options in templates
      const formattedQuestions = questions.map(question => ({
        ...question,
        required: question.is_required === true ? 1 : 0,
        image_required: question.image_required === true ? 1 : 0,
        allow_multiple_selections: question.allow_multiple_selections === true ? 1 : 0,
        min_selections: question.min_selections || null,
        max_selections: question.max_selections || null,
        options: dedupeOptionStrings(question.options),
      }));

      logger.debug('🔍 Debug - Original questions state:', questions);
      logger.debug('🔍 Debug - Formatted questions for backend:', formattedQuestions);
      logger.debug('🔍 Debug - Multi-select fields in first question:', formattedQuestions.length > 0 ? {
        allow_multiple_selections: formattedQuestions[0].allow_multiple_selections,
        min_selections: formattedQuestions[0].min_selections,
        max_selections: formattedQuestions[0].max_selections
      } : 'No questions');

      // Auto-add loan-specific questions if template is tagged with 'loan'
      let finalQuestions = formattedQuestions;
      if (finalTemplateTags.includes('loan')) {
        finalQuestions = addLoanSpecificQuestions(formattedQuestions);
      }

      logger.debug('🔍 Debug - Final questions being sent:', finalQuestions);
      logger.debug('🔍 Debug - Template payload:', {
        template_name: templateName,
        description: templateDescription,
        asset_type_ids: selectedAssetTypeIds,
        questions: finalQuestions,
        tags: finalTemplateTags,
      });

      const assetTypeIdsForSave = finalTemplateTags.includes('location') ? [] : selectedAssetTypeIds;
      const suggested_frequency = suggestedFrequencyEnabled
        ? suggestedFrequencyByAssetType
        : assetTypeIdsForSave.reduce((acc, id) => ({ ...acc, [id]: { value: null, unit: null } }), {} as Record<string, { value: null; unit: null }>);
      const response = await axios.put(
        `${API_BASE_URL}/check-templates/${selectedTemplate.template_id}`,
        {
          template_name: templateName,
          description: templateDescription,
          asset_type_ids: assetTypeIdsForSave,
          questions: finalQuestions,
          tags: finalTemplateTags,
          is_global: isGlobal,
          force_condition_images: forceConditionImages,
          min_condition_images: minConditionImages,
          allow_skip_dvla: finalTemplateTags.includes('location') ? false : allowSkipDvla,
          allow_email_completion: finalTemplateTags.includes('location') ? false : allowEmailCompletion,
          requires_section_6_8_waiver: finalTemplateTags.includes('delivery') ? requiresSection68Waiver : false,
          section_6_8_waiver_text: finalTemplateTags.includes('delivery') && requiresSection68Waiver
            ? (section68WaiverText && section68WaiverText.trim() ? section68WaiverText : null)
            : null,
          requires_whole_good_number: finalTemplateTags.includes('delivery') ? requiresWholeGoodNumber : false,
          whole_good_number_prefix: finalTemplateTags.includes('delivery') && requiresWholeGoodNumber
            ? (wholeGoodNumberPrefix.trim().toUpperCase() || 'WGS')
            : null,
          suggested_frequency,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      logger.debug('🔍 Debug - Backend response:', response.data);

      if (response.data) {
        if (response.data.success !== false) {
          await fetchTemplates();
          setEditDialogOpen(false);
          resetForm();
          setSnackbar({
            open: true,
            message: 'Template updated successfully',
            severity: 'success'
          });
          return;
        }
      }

      setSnackbar({ open: true, message: 'Failed to update template. Please try again.', severity: 'error' });
    } catch (error) {
      console.error('Error updating template:', error);
      const { message, detail } = buildSaveErrorMessage(error);
      setSnackbar({ open: true, message, detail, severity: 'error' });
    }
  };

  /**
   * Converts a raw Axios/API error into a user-friendly snackbar message + optional detail.
   */
  const buildSaveErrorMessage = (error: unknown): { message: string; detail: string } => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const serverMsg: string = error.response?.data?.message || error.response?.data?.error || '';

      if (status === 401) {
        return {
          message: 'Your session has expired',
          detail: 'Please reload the page and log in again, then try saving.',
        };
      }
      if (status === 403) {
        if (serverMsg.toLowerCase().includes('global template') || serverMsg.toLowerCase().includes('demo check now')) {
          return {
            message: 'This is a platform-managed template',
            detail: 'Global templates can only be edited by the platform. You can assign asset types to it.',
          };
        }
        if (serverMsg.toLowerCase().includes('permission') || serverMsg.toLowerCase().includes('business')) {
          return {
            message: 'Permission denied',
            detail: 'This template belongs to a different business account. Contact your administrator if you need access.',
          };
        }
        return {
          message: 'Permission denied',
          detail: serverMsg || 'You do not have permission to edit this template.',
        };
      }
      if (status === 404) {
        return {
          message: 'Template not found',
          detail: 'This template may have been deleted. Refresh the page to reload the latest templates.',
        };
      }
      if (status && status >= 500) {
        return {
          message: 'Server error — template was not saved',
          detail: 'Something went wrong on the server. Please try again or contact support if this keeps happening.',
        };
      }
      if (serverMsg) {
        return { message: 'Failed to save template', detail: serverMsg };
      }
    }
    return {
      message: 'Failed to save template',
      detail: 'An unexpected error occurred. Please try again.',
    };
  };

  /**
   * Save handler for the ModernTemplateBuilder edit dialog.
   * Routes to an asset-type-only update for global templates (non-Demo-Check-Now businesses
   * are not permitted to modify global template content) and to the full update otherwise.
   */
  const handleSaveEditedTemplate = async () => {
    if (isGlobal && !canEditGlobalTemplate()) {
      try {
        const token = localStorage.getItem('token');
        if (!token || !selectedTemplate) {
          setSnackbar({ open: true, message: 'Cannot save — missing session or template', detail: 'Please reload the page and try again.', severity: 'error' });
          return;
        }
        await axios.post(
          `${API_BASE_URL}/check-templates/${selectedTemplate.template_id}/asset-types`,
          { assetTypeIds: selectedAssetTypeIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchTemplates();
        setEditDialogOpen(false);
        resetForm();
        setSnackbar({ open: true, message: 'Asset types updated successfully', severity: 'success' });
      } catch (err: unknown) {
        console.error('Error updating asset types:', err);
        const { message, detail } = buildSaveErrorMessage(err);
        setSnackbar({ open: true, message, detail, severity: 'error' });
      }
    } else {
      await handleUpdateTemplate();
    }
  };

  const addLoanSpecificQuestions = (existingQuestions: any[]) => {
    const loanQuestions = [
      {
        question_text: '--- DAMAGE & CONDITION REPORT ---',
        question_type: 'text',
        required: 0,
        tags: ['section_header']
      },
      {
        question_text: 'Visual Inspection Completed',
        question_type: 'yes_no',
        required: 1,
        tags: ['condition_report']
      },
      {
        question_text: 'Condition Report Notes',
        question_type: 'text',
        required: 0,
        tags: ['condition_report']
      },
      // Terms are now handled via UserAgreements system
      {
        question_text: '--- SIGNATURES ---',
        question_type: 'text',
        required: 0,
        tags: ['section_header']
      },
      {
        question_text: 'Customer Signature',
        question_type: 'signature',
        required: 1,
        tags: ['signatures']
      },
      {
        question_text: 'Business Representative Signature',
        question_type: 'signature',
        required: 1,
        tags: ['signatures']
      }
    ];

    // Build a set of loan question texts to avoid duplicating them on repeated saves/auto-saves
    const loanTexts = new Set(loanQuestions.map(q => q.question_text.trim().toLowerCase()));

    // Strip any existing copies of loan-specific questions so they always appear exactly once
    const customQuestions = existingQuestions.filter(
      q => !loanTexts.has((q.question_text || '').trim().toLowerCase())
    );

    return [...customQuestions, ...loanQuestions];
  };

  const handleAddQuestion = () => {
    if (newQuestion.question_text.trim()) {
      // Prevent adding signature questions for loan check types
      if (templateTags.includes('loan') && newQuestion.question_type === 'signature') {
        setSnackbar({ open: true, message: 'Signature questions cannot be added to loan check templates. They are automatically included.', severity: 'error' });
        return;
      }
      let questionToAdd: Question = { ...newQuestion };
      if (questionToAdd.question_type === 'multiple_choice') {
        const cleaned = (questionToAdd.options || []).map(o => (o || '').trim()).filter(Boolean);
        questionToAdd.options = cleaned;
        
        // Validate multi-select settings
        if (questionToAdd.allow_multiple_selections) {
          const optionCount = cleaned.length;
          
          if (questionToAdd.min_selections !== undefined && questionToAdd.min_selections < 1) {
            setSnackbar({ open: true, message: 'Minimum selections must be at least 1', severity: 'error' });
            return;
          }
          if (questionToAdd.min_selections !== undefined && questionToAdd.min_selections > optionCount) {
            setSnackbar({ open: true, message: `Minimum selections (${questionToAdd.min_selections}) cannot exceed number of options (${optionCount})`, severity: 'error' });
            return;
          }
          if (questionToAdd.max_selections !== undefined && questionToAdd.max_selections < 1) {
            setSnackbar({ open: true, message: 'Maximum selections must be at least 1', severity: 'error' });
            return;
          }
          if (questionToAdd.max_selections !== undefined && questionToAdd.max_selections > optionCount) {
            setSnackbar({ open: true, message: `Maximum selections (${questionToAdd.max_selections}) cannot exceed number of options (${optionCount})`, severity: 'error' });
            return;
          }
          if (questionToAdd.min_selections !== undefined && questionToAdd.max_selections !== undefined && questionToAdd.min_selections > questionToAdd.max_selections) {
            setSnackbar({ open: true, message: 'Minimum selections cannot be greater than maximum selections', severity: 'error' });
            return;
          }
        }
      }
      setQuestions([...questions, questionToAdd]);
      setNewQuestion({
        question_text: '',
        question_type: 'yes_no_na',
        is_required: true,
        image_required: false,
        force_image_on_yes: false,
        force_image_on_no: false,
        hide_on_return_if_yes: false,
        hide_on_return_if_no: false,
        hide_on_return_if_na: false,
        allow_multiple_selections: false,
        min_selections: undefined,
        max_selections: undefined,
      });
    }
  };

  const handleEditQuestion = (index: number) => {
    setCurrentEditQuestion({ ...questions[index] });
    setEditQuestionIndex(index);
    setEditQuestionDialogOpen(true);
  };

  const handleSaveEditedQuestion = () => {
    if (editQuestionIndex !== null && currentEditQuestion) {
      // Prevent saving signature questions for loan check types
      if (templateTags.includes('loan') && currentEditQuestion.question_type === 'signature') {
        setSnackbar({ open: true, message: 'Signature questions cannot be added to loan check templates. They are automatically included.', severity: 'error' });
        return;
      }
      let updatedQuestion = { ...currentEditQuestion } as Question;
      if (updatedQuestion.question_type === 'multiple_choice') {
        const cleaned = (updatedQuestion.options || []).map(o => (o || '').trim()).filter(Boolean);
        if (cleaned.length < 2) {
          setSnackbar({ open: true, message: 'Please add at least 2 options for multiple choice', severity: 'error' });
          return;
        }
        updatedQuestion.options = cleaned;
        
        // Validate multi-select settings
        if (updatedQuestion.allow_multiple_selections) {
          const optionCount = cleaned.length;
          
          if (updatedQuestion.min_selections !== undefined && updatedQuestion.min_selections < 1) {
            setSnackbar({ open: true, message: 'Minimum selections must be at least 1', severity: 'error' });
            return;
          }
          if (updatedQuestion.min_selections !== undefined && updatedQuestion.min_selections > optionCount) {
            setSnackbar({ open: true, message: `Minimum selections (${updatedQuestion.min_selections}) cannot exceed number of options (${optionCount})`, severity: 'error' });
            return;
          }
          if (updatedQuestion.max_selections !== undefined && updatedQuestion.max_selections < 1) {
            setSnackbar({ open: true, message: 'Maximum selections must be at least 1', severity: 'error' });
            return;
          }
          if (updatedQuestion.max_selections !== undefined && updatedQuestion.max_selections > optionCount) {
            setSnackbar({ open: true, message: `Maximum selections (${updatedQuestion.max_selections}) cannot exceed number of options (${optionCount})`, severity: 'error' });
            return;
          }
          if (updatedQuestion.min_selections !== undefined && updatedQuestion.max_selections !== undefined && updatedQuestion.min_selections > updatedQuestion.max_selections) {
            setSnackbar({ open: true, message: 'Minimum selections cannot be greater than maximum selections', severity: 'error' });
            return;
          }
        }
      }
      const updatedQuestions = [...questions];
      updatedQuestions[editQuestionIndex] = updatedQuestion;
      setQuestions(updatedQuestions);
      setEditQuestionDialogOpen(false);
      setEditQuestionIndex(null);
      setCurrentEditQuestion(null);
    }
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  // New functions for manual ordering
  const moveQuestionUp = (index: number) => {
    if (index === 0) return; // Already at the top
    
    const updatedQuestions = [...questions];
    const temp = updatedQuestions[index];
    updatedQuestions[index] = updatedQuestions[index - 1];
    updatedQuestions[index - 1] = temp;
    
    setQuestions(updatedQuestions);
  };

  const moveQuestionDown = (index: number) => {
    if (index === questions.length - 1) return; // Already at the bottom
    
    const updatedQuestions = [...questions];
    const temp = updatedQuestions[index];
    updatedQuestions[index] = updatedQuestions[index + 1];
    updatedQuestions[index + 1] = temp;
    
    setQuestions(updatedQuestions);
  };

  const handleEditTemplate = async (template: CheckTemplate) => {
    // Allow editing dialog to open for all; restrict actions inside if needed
 
    try {
      setSelectedTemplate(template);
      setTemplateName(template.template_name);
      setTemplateDescription(template.description);
      setTemplateTags(template.tags || []);
      setSelectedAssetTypeIds(template.asset_types?.map(at => at.asset_type_id) || []);
      setIsGlobal(template.is_global || false);
      setForceConditionImages(template.force_condition_images || false);
      setMinConditionImages(template.min_condition_images || 0);
      setAllowEmailCompletion(template.allow_email_completion || false);
      setAllowSkipDvla(!!template.allow_skip_dvla);
      setRequiresSection68Waiver(!!(template as any).requires_section_6_8_waiver);
      setSection68WaiverText((template as any).section_6_8_waiver_text || '');
      setRequiresWholeGoodNumber(!!(template as any).requires_whole_good_number);
      const _wgnPfx1 = ((template as any).whole_good_number_prefix || '').trim().toUpperCase();
      setWholeGoodNumberPrefix(_wgnPfx1 === 'WGS' ? '' : _wgnPfx1);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      // Fetch full template details including asset types & questions
      const response = await axios.get(`${API_BASE_URL}/check-templates/${template.template_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        const t = response.data;
        setTemplateName(t.template_name);
        setTemplateDescription(t.description);
        setTemplateTags(Array.isArray(t.tags) ? t.tags : []);
        setSelectedAssetTypeIds((t.asset_types || []).map((at: any) => at.asset_type_id));
        const freqByAssetType: Record<string, { value: number; unit: string }> = {};
        let anyEnabled = false;
        (t.asset_types || []).forEach((at: any) => {
          if (at.asset_type_id != null) {
            const hasValue = at.suggested_frequency_value != null && at.suggested_frequency_value !== '';
            if (hasValue) anyEnabled = true;
            freqByAssetType[at.asset_type_id] = {
              value: hasValue ? Number(at.suggested_frequency_value) : 1,
              unit: at.suggested_frequency_unit || 'weeks',
            };
          }
        });
        setSuggestedFrequencyEnabled(anyEnabled);
        setSuggestedFrequencyByAssetType(freqByAssetType);
        setAllowEmailCompletion(t.allow_email_completion === 1 || t.allow_email_completion === true);
        setAllowSkipDvla(t.allow_skip_dvla === 1 || t.allow_skip_dvla === true);
        setRequiresSection68Waiver(t.requires_section_6_8_waiver === 1 || t.requires_section_6_8_waiver === true);
        setSection68WaiverText(t.section_6_8_waiver_text || '');
        setRequiresWholeGoodNumber(t.requires_whole_good_number === 1 || t.requires_whole_good_number === true);
        const _wgnPfx2 = (t.whole_good_number_prefix || '').trim().toUpperCase();
        setWholeGoodNumberPrefix(_wgnPfx2 === 'WGS' ? '' : _wgnPfx2);
        const normalizedQuestions: Question[] = (t.questions || [])
          .filter((q: any) => {
            const rawTags = Array.isArray(q.tags) ? q.tags : [];
            const tagSet = rawTags.map((x: any) => String(x).toLowerCase());
            return !(tagSet.includes('loan_screen_2') || tagSet.includes('loan_screen_3'));
          })
          .map((q: any) => ({
          question_id: q.question_id,
          question_text: q.question_text,
          question_type: q.question_type,
          is_required: q.required === 1 || q.required === true,
          image_required: q.image_required === 1 || q.image_required === true,
          force_image_on_yes: q.force_image_on_yes === 1 || q.force_image_on_yes === true,
          force_image_on_no: q.force_image_on_no === 1 || q.force_image_on_no === true,
          hide_on_return_if_yes: q.hide_on_return_if_yes === 1 || q.hide_on_return_if_yes === true,
          hide_on_return_if_no: q.hide_on_return_if_no === 1 || q.hide_on_return_if_no === true,
          hide_on_return_if_na: q.hide_on_return_if_na === 1 || q.hide_on_return_if_na === true,
          show_conditions: q.show_conditions,
          options: extractOptionTexts(q.options),
          tags: Array.isArray(q.tags) ? q.tags.map((x: any) => String(x)) : [],
          allow_multiple_selections: q.allow_multiple_selections === 1 || q.allow_multiple_selections === true,
          min_selections: q.min_selections || undefined,
          max_selections: q.max_selections || undefined,
        }));
        
        logger.debug('🎯 Loaded questions for editing with conditional logic:', 
          normalizedQuestions.filter(q => q.show_conditions).map(q => ({
            text: q.question_text.substring(0, 30),
            conditions: q.show_conditions
          }))
        );
        
        setQuestions(normalizedQuestions);
      }

      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error loading template for edit:', error);
      setError('Failed to load template');
    }
  };

  const openSendEmailDialog = (template: CheckTemplate) => {
    setSendEmailTemplate(template);
    setSendEmailRecipientEmail('');
    setSendEmailRecipientName('');
    setSendEmailCustomMessage('');
    setSendEmailDialogOpen(true);
  };

  const handleSendEmailCheck = async () => {
    if (!sendEmailTemplate || !sendEmailRecipientEmail.trim()) return;
    setSendEmailLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/email-checks/send`,
        {
          templateId: sendEmailTemplate.template_id,
          recipientEmail: sendEmailRecipientEmail.trim(),
          recipientName: sendEmailRecipientName.trim() || undefined,
          customMessage: sendEmailCustomMessage.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendEmailDialogOpen(false);
      setSnackbar({ open: true, message: 'Check email sent successfully!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to send email. Please try again.',
        severity: 'error',
      });
    } finally {
      setSendEmailLoading(false);
    }
  };

  const handleArchive = async (templateId: string) => {
    // Find the template to check if it's global
    const template = templates.find(t => t.template_id === templateId);
    if (!template) {
      setError('Template not found');
      return;
    }

    // Check if template is global and prevent deletion (except for Super Admin, Owner, or Demo Check Now business)
    // Removed unused userRole variable
    // const userRole = localStorage.getItem('userRole');
    // Allow any authenticated user to archive global templates

    if (window.confirm('Are you sure you want to delete this template? It will be moved to archived.')) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        logger.debug('Original template tags:', template.tags, 'Type:', typeof template.tags);

        // Parse tags if they're a JSON string, otherwise use as array
        let currentTags: string[] = [];
        if (typeof template.tags === 'string') {
          try {
            currentTags = JSON.parse(template.tags);
          } catch (e) {
            console.warn('Failed to parse tags as JSON, treating as string:', template.tags);
            currentTags = template.tags ? [template.tags] : [];
          }
        } else if (Array.isArray(template.tags)) {
          currentTags = template.tags;
        } else {
          currentTags = [];
        }

        logger.debug('Parsed current tags:', currentTags);

        // Add "deleted" tag to the existing tags
        const updatedTags = [...currentTags];
        if (!updatedTags.includes('deleted')) {
          updatedTags.push('deleted');
        }

        logger.debug('Updated tags to send:', updatedTags);

        const response = await axios.put(
          `${API_BASE_URL}/check-templates/${templateId}`,
          {
            template_name: template.template_name,
            description: template.description,
            tags: updatedTags,
            is_active: false
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.data.success) {
          fetchTemplates();
          setSnackbar({
            open: true,
            message: 'Template deleted successfully',
            severity: 'success'
          });
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete template',
          severity: 'error'
        });
      }
    }
  };

  const parseTemplateTags = (tags: string[] | string | undefined): string[] => {
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed : tags ? [tags] : [];
      } catch {
        return tags ? [tags] : [];
      }
    }
    if (Array.isArray(tags)) {
      return tags;
    }
    return [];
  };

  const handleUnarchive = async (templateId: string) => {
    const template = templates.find(t => t.template_id === templateId);
    if (!template) {
      setError('Template not found');
      return;
    }

    if (window.confirm('Restore this template? It will be moved back to your active templates.')) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const currentTags = parseTemplateTags(template.tags);
        const updatedTags = currentTags.filter(tag => tag !== 'deleted');

        const response = await axios.put(
          `${API_BASE_URL}/check-templates/${templateId}`,
          {
            template_name: template.template_name,
            description: template.description,
            tags: updatedTags,
            is_active: true,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          fetchTemplates();
          setSnackbar({
            open: true,
            message: 'Template restored successfully',
            severity: 'success',
          });
        }
      } catch (error) {
        console.error('Error restoring template:', error);
        setSnackbar({
          open: true,
          message: 'Failed to restore template',
          severity: 'error',
        });
      }
    }
  };

  // ── JSON Import ──────────────────────────────────────────────────────────────

  const VALID_QUESTION_TYPES: QuestionType[] = [
    'text', 'multiple_choice', 'yes_no', 'yes_no_na', 'number', 'date',
    'signature', 'text_header', 'email_check', 'image_type',
  ];

  const openImportDialog = () => {
    setImportJsonText('');
    setImportParsed(null);
    setImportErrors([]);
    setImportWarnings([]);
    setImportStep('input');
    setImportDialogOpen(true);
  };

  const handleImportFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImportJsonText((ev.target?.result as string) || '');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const validateImportJson = (): boolean => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let parsed: any;

    try {
      parsed = JSON.parse(importJsonText);
    } catch {
      setImportErrors(['Invalid JSON — please check the syntax and try again.']);
      return false;
    }

    if (!parsed.template_name || typeof parsed.template_name !== 'string' || !parsed.template_name.trim()) {
      errors.push('template_name is required and must be a non-empty string.');
    }

    if (parsed.description !== undefined && typeof parsed.description !== 'string') {
      warnings.push('description should be a string; it will be ignored.');
    }

    const validTags = ['safety', 'loan', 'delivery', 'location'];
    const tags: string[] = Array.isArray(parsed.tags) ? parsed.tags : [];
    const checkTypeTags = tags.filter((t: string) => validTags.includes(t));
    if (checkTypeTags.includes('location') && !businessPermissions.allowLocationChecks) {
      errors.push('Location/site check templates require Location Checks to be enabled for this business.');
    }
    if (checkTypeTags.length === 0) {
      warnings.push('No check type tag found (safety / loan / delivery / location) — will default to the current tab.');
    }

    if (!Array.isArray(parsed.questions) || parsed.questions.length === 0) {
      errors.push('questions must be a non-empty array.');
    } else {
      parsed.questions.forEach((q: any, i: number) => {
        const prefix = `Question ${i + 1}`;
        if (!q.question_text || typeof q.question_text !== 'string') {
          errors.push(`${prefix}: question_text is required.`);
        }
        if (!VALID_QUESTION_TYPES.includes(q.question_type)) {
          errors.push(`${prefix}: question_type "${q.question_type}" is not valid. Must be one of: ${VALID_QUESTION_TYPES.join(', ')}.`);
        }
        if (q.question_type === 'multiple_choice') {
          if (!Array.isArray(q.options) || q.options.length < 2) {
            errors.push(`${prefix}: multiple_choice questions need at least 2 options.`);
          }
          if (q.allow_multiple_selections) {
            const optCount = Array.isArray(q.options) ? q.options.length : 0;
            if (q.min_selections !== undefined && q.min_selections < 1) {
              errors.push(`${prefix}: min_selections must be at least 1.`);
            }
            if (q.max_selections !== undefined && q.max_selections < 1) {
              errors.push(`${prefix}: max_selections must be at least 1.`);
            }
            if (q.min_selections !== undefined && q.max_selections !== undefined && q.min_selections > q.max_selections) {
              errors.push(`${prefix}: min_selections cannot exceed max_selections.`);
            }
            if (q.min_selections !== undefined && q.min_selections > optCount) {
              errors.push(`${prefix}: min_selections cannot exceed number of options (${optCount}).`);
            }
            if (q.max_selections !== undefined && q.max_selections > optCount) {
              errors.push(`${prefix}: max_selections cannot exceed number of options (${optCount}).`);
            }
          }
        }
        if (q.show_conditions !== undefined && q.show_conditions !== null && typeof q.show_conditions !== 'object') {
          warnings.push(`${prefix}: show_conditions should be an object or null.`);
        }
      });
    }

    setImportErrors(errors);
    setImportWarnings(warnings);
    if (errors.length > 0) return false;
    setImportParsed(parsed);
    return true;
  };

  const handleImportValidate = () => {
    if (validateImportJson()) {
      setImportStep('preview');
    }
  };

  const handleImportSubmit = async () => {
    if (!importParsed) return;
    setImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const p = importParsed;

      // Build tags — preserve what was in JSON; inject current tab's tag if none present
      let tags: string[] = Array.isArray(p.tags) ? p.tags.filter((t: string) => t !== 'deleted') : [];
      if (!tags.some((t: string) => CHECK_TYPE_TAGS.includes(t as typeof CHECK_TYPE_TAGS[number]))) {
        tags = [filterTag || 'safety', ...tags];
      }
      tags = finalizeTemplateTags(tags, businessPermissions, filterTag);

      const formattedQuestions = (p.questions || []).map((q: any, idx: number) => ({
        question_text: q.question_text,
        question_type: q.question_type,
        required: q.is_required === true || q.required === 1 ? 1 : 0,
        image_required: q.image_required === true || q.image_required === 1 ? 1 : 0,
        force_image_on_yes: q.force_image_on_yes === true || q.force_image_on_yes === 1 ? 1 : 0,
        force_image_on_no: q.force_image_on_no === true || q.force_image_on_no === 1 ? 1 : 0,
        hide_on_return_if_yes: q.hide_on_return_if_yes === true || q.hide_on_return_if_yes === 1 ? 1 : 0,
        hide_on_return_if_no: q.hide_on_return_if_no === true || q.hide_on_return_if_no === 1 ? 1 : 0,
        hide_on_return_if_na: q.hide_on_return_if_na === true || q.hide_on_return_if_na === 1 ? 1 : 0,
        show_conditions: q.show_conditions ?? null,
        options: Array.isArray(q.options) ? Array.from(new Set(q.options.map((o: any) => String(o).trim()).filter(Boolean))) : [],
        allow_multiple_selections: q.allow_multiple_selections === true || q.allow_multiple_selections === 1 ? 1 : 0,
        min_selections: q.min_selections ?? null,
        max_selections: q.max_selections ?? null,
        order_number: idx + 1,
      }));

      let finalQuestions = formattedQuestions;
      if (tags.includes('loan')) {
        finalQuestions = addLoanSpecificQuestions(formattedQuestions);
      }

      await axios.post(
        `${API_BASE_URL}/check-templates`,
        {
          template_name: p.template_name.trim(),
          description: typeof p.description === 'string' ? p.description.trim() : '',
          tags,
          questions: finalQuestions,
          asset_type_ids: [],
          is_global: p.is_global === true,
          force_condition_images: p.force_condition_images === true,
          min_condition_images: typeof p.min_condition_images === 'number' ? p.min_condition_images : 0,
          allow_email_completion: p.allow_email_completion === true,
          allow_skip_dvla: p.allow_skip_dvla === true,
          requires_section_6_8_waiver: p.requires_section_6_8_waiver === true,
          section_6_8_waiver_text: p.section_6_8_waiver_text ?? null,
          requires_whole_good_number: p.requires_whole_good_number === true,
          whole_good_number_prefix: p.requires_whole_good_number === true
            ? (String(p.whole_good_number_prefix || 'WGS').trim().toUpperCase() || 'WGS')
            : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setImportStep('done');
      fetchTemplates();
    } catch (err: any) {
      setImportErrors([err?.response?.data?.message || err.message || 'Import failed. Please try again.']);
    } finally {
      setImportLoading(false);
    }
  };

  // ── End JSON Import ───────────────────────────────────────────────────────────

  const handleAssetTypeCreated = async (newAssetType: { asset_type_id: string; type_name: string }) => {
    logger.debug('Asset type created in CheckTemplateManagement:', newAssetType);
    
    // Keep track of the name so we can find it after refresh
    const newTypeName = newAssetType.type_name;
    
    // Close the dialog immediately for better UX
    setCreateAssetTypeDialogOpen(false);
    
    try {
      // Immediately fetch the updated asset types list from the server
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/asset-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Update the asset types with the fresh data from the server
        const updatedAssetTypes = response.data.data;
        setAssetTypes(updatedAssetTypes);
        
        // Find the newly created asset type by name
        const createdAssetType = updatedAssetTypes.find((type: AssetType) => type.type_name === newTypeName);
        
        if (createdAssetType) {
          // Auto-select the newly created asset type
          setSelectedAssetTypeIds(prev => {
            if (prev.includes(createdAssetType.asset_type_id)) {
              return prev;
            }
            return [...prev, createdAssetType.asset_type_id];
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing asset types:', error);
    }
  };

  const filteredTemplates = templates.filter(template => 
    showArchived ? template.tags?.includes('deleted') : !template.tags?.includes('deleted')
  );
  const isLocationTab = filterTag === 'location';
  const locationTargetLabel = locationLabel(businessPermissions.useSiteTerminology, { plural: true });

  // Debug logging for filtering
  logger.debug('Filtering templates:');
  logger.debug('- Total templates:', templates.length);
  logger.debug('- Show archived:', showArchived);
  logger.debug('- Templates with deleted tag:', templates.filter(t => t.tags?.includes('deleted')).length);
  logger.debug('- Templates without deleted tag:', templates.filter(t => !t.tags?.includes('deleted')).length);
  logger.debug('- Filtered templates count:', filteredTemplates.length);
  logger.debug('- All template tags:', templates.map(t => ({ id: t.template_id, name: t.template_name, tags: t.tags })));

  const renderQuestionList = () => {
    return (
      <List 
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 0,
          mb: 2
        }}
      >
        {questions.length === 0 ? (
          <ListItem>
            <ListItemText 
              primary="No questions added yet"
              secondary="Add questions using the form below"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}
            />
          </ListItem>
        ) : (
          questions.map((question, index) => (
            <ListItem
              key={`question-${index}`}
              sx={{
                border: '1px solid',
                borderColor: question.question_type === 'text_header' ? 'primary.main' : 'divider',
                borderRadius: 1,
                mb: 1,
                mx: 1,
                mt: index === 0 ? 1 : 0,
                bgcolor: question.question_type === 'text_header' ? 'primary.50' : 'white',
                position: 'relative',
                '&:hover': {
                  bgcolor: question.question_type === 'text_header' ? 'primary.100' : 'rgba(0, 0, 0, 0.01)',
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: -32,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => moveQuestionUp(index)}
                  disabled={index === 0}
                  sx={{ 
                    color: index === 0 ? 'text.disabled' : 'primary.main',
                    '&:hover': { bgcolor: index === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveQuestionDown(index)}
                  disabled={index === questions.length - 1}
                  sx={{ 
                    color: index === questions.length - 1 ? 'text.disabled' : 'primary.main',
                    '&:hover': { bgcolor: index === questions.length - 1 ? 'transparent' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    <Typography 
                      variant={question.question_type === 'text_header' ? "h6" : "subtitle1"} 
                      component="span" 
                      fontWeight={question.question_type === 'text_header' ? "bold" : "medium"}
                      sx={{
                        color: question.question_type === 'text_header' ? 'primary.main' : 'inherit'
                      }}
                    >
                      {question.question_type === 'text_header' ? '📋 ' : `${index + 1}. `}{question.question_text}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box mt={0.5} display="flex" flexWrap="wrap" gap={1}>
                    <Chip 
                      size="small" 
                      label={question.question_type === 'text_header' ? 'Section Header' : `Type: ${question.question_type.replace('_', '/')}`} 
                      color={question.question_type === 'text_header' ? 'secondary' : 'primary'} 
                      variant="outlined"
                    />
                    {question.question_type !== 'text_header' && (
                      <>
                        <Chip 
                          size="small" 
                          label={`Required: ${question.is_required ? 'Yes' : 'No'}`} 
                          color={question.is_required ? "success" : "default"} 
                          variant="outlined"
                        />
                        {question.image_required && (
                          <Chip 
                            size="small" 
                            label="Image Required" 
                            color="warning" 
                            variant="outlined"
                          />
                        )}
                      </>
                    )}
                    {question.question_type === 'multiple_choice' && Array.isArray(question.options) && question.options.length > 0 && (
                      <Chip
                        size="small"
                        label={`Options: ${question.options.length}`}
                        variant="outlined"
                      />
                    )}
                    {question.question_type === 'multiple_choice' && question.allow_multiple_selections && (
                      <Chip
                        size="small"
                        label={
                          question.min_selections && question.max_selections
                            ? `Multi-Select: ${question.min_selections}-${question.max_selections} options`
                            : question.min_selections
                              ? `Multi-Select: At least ${question.min_selections} options`
                              : question.max_selections
                                ? `Multi-Select: Up to ${question.max_selections} options`
                                : 'Multi-Select: Any number'
                        }
                        color="info"
                        variant="filled"
                      />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleEditQuestion(index)} sx={{ mr: 1 }}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" onClick={() => handleRemoveQuestion(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        )}
      </List>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  if (filterTag === 'location' && !businessPermissions.allowLocationChecks) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowArchived(!showArchived)}
          sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
        >
          {showArchived ? 'Show Active' : 'Show Archived'}
        </Button>
        {businessPermissions.allowTemplateCreation && canManageTemplates && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={openImportDialog}
              sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
            >
              Import JSON
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setCreateDialogOpen(true);
              }}
              sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
            >
              Create Template
            </Button>
            <Tooltip
              title={
                !businessPermissions.allowAiTemplateGeneration
                  ? 'This feature is locked for your account. Please contact your account manager to enable AI template generation.'
                  : ''
              }
              disableHoverListener={businessPermissions.allowAiTemplateGeneration}
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiGeneratorOpen(true)}
                  disabled={!businessPermissions.allowAiTemplateGeneration}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    background: businessPermissions.allowAiTemplateGeneration
                      ? 'linear-gradient(135deg, #7c3aed, #a855f7)'
                      : undefined,
                    color: businessPermissions.allowAiTemplateGeneration ? '#fff' : undefined,
                    fontWeight: 600,
                    '&:hover': {
                      background: businessPermissions.allowAiTemplateGeneration
                        ? 'linear-gradient(135deg, #6d28d9, #9333ea)'
                        : undefined,
                    },
                  }}
                >
                  Generate with AI
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {businessPermissions.allowTemplateCreation && !canManageTemplates && (
          <Tooltip title="You do not have permission to create templates">
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                disabled
                sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' }, opacity: 0.5 }}
              >
                Create Template
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {!businessPermissions.allowTemplateCreation && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Template creation is currently disabled for your business. Please contact your administrator to enable this feature.
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredTemplates.map((template) => (
            <Paper key={template.template_id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {template.template_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {template.description}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  {template.tags?.includes('deleted') ? (
                    <Tooltip title={template.is_global && !canEditGlobalTemplate() ? 'Cannot restore global template - Super Admin or Owner role required' : 'Restore template'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleUnarchive(template.template_id)}
                          color="primary"
                          disabled={template.is_global && !canEditGlobalTemplate()}
                        >
                          <UnarchiveIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      {Boolean(template.allow_email_completion) && !isLocationTab && (
                        <Tooltip title="Send via Email">
                          <IconButton
                            size="small"
                            onClick={() => openSendEmailDialog(template)}
                            color="info"
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={!canManageTemplates ? "You do not have permission to edit templates" : (template.is_global && !canEditGlobalTemplate() ? (isLocationTab ? "Edit template" : "Assign asset types to this global template") : "Edit template")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canManageTemplates) return;
                              handleEditTemplate(template);
                            }}
                            color="primary"
                            disabled={!canManageTemplates}
                            sx={{ opacity: !canManageTemplates ? 0.5 : 1, cursor: !canManageTemplates ? 'not-allowed' : 'pointer' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleArchive(template.template_id)}
                        color="error"
                        disabled={template.is_global && !canEditGlobalTemplate()}
                        title={template.is_global && !canEditGlobalTemplate() ? "Cannot delete global template - Super Admin or Owner role required" : "Delete template"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isLocationTab ? (
                  <>
                    <Typography variant="body2">
                      <strong>Target:</strong> {locationTargetLabel}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Questions:</strong> {template.question_count ?? template.questions?.length ?? 0}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">
                    <strong>Asset Types:</strong>{' '}
                    {template.asset_types?.map(at => at.type_name).join(', ') || 'None'}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={template.tags?.includes('deleted') ? 'Archived' : template.is_active ? 'Active' : 'Inactive'}
                    color={template.tags?.includes('deleted') ? 'default' : template.is_active ? 'success' : 'error'}
                    size="small"
                  />
                  {Boolean(template.is_global) && (
                    <Chip
                      label="Global"
                      color="secondary"
                      size="small"
                      sx={{ bgcolor: 'secondary.light', color: 'secondary.dark' }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1.5,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={sectionLabelSx}>Name</TableCell>
                {isLocationTab ? (
                  <>
                    <TableCell sx={sectionLabelSx}>Target</TableCell>
                    <TableCell sx={sectionLabelSx}>Questions</TableCell>
                  </>
                ) : (
                  <TableCell sx={sectionLabelSx}>Asset Types</TableCell>
                )}
                <TableCell sx={sectionLabelSx}>Status</TableCell>
                <TableCell align="right" sx={sectionLabelSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.template_id} hover>
                  <TableCell sx={{ py: 1.25 }}>
                    <Typography sx={sectionTitleSx}>{template.template_name}</Typography>
                    {template.description ? (
                      <Typography
                        sx={{
                          fontFamily: lato,
                          fontSize: '0.8rem',
                          color: 'text.secondary',
                          mt: 0.25,
                        }}
                      >
                        {template.description}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {isLocationTab ? (
                    <>
                      <TableCell sx={{ py: 1.25, fontFamily: lato, fontSize: '0.845rem' }}>
                        {locationTargetLabel}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, fontFamily: lato, fontSize: '0.845rem' }}>
                        {template.question_count ?? template.questions?.length ?? 0}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ py: 1.25, fontFamily: lato, fontSize: '0.845rem' }}>
                      {template.asset_types?.map(at => at.type_name).join(', ') || '—'}
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={template.tags?.includes('deleted') ? 'Archived' : template.is_active ? 'Active' : 'Inactive'}
                        color={template.tags?.includes('deleted') ? 'default' : template.is_active ? 'success' : 'error'}
                        size="small"
                      />
                      {Boolean(template.is_global) && (
                        <Chip
                          label="Global"
                          color="secondary"
                          size="small"
                          sx={{ bgcolor: 'secondary.light', color: 'secondary.dark' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.25 }}>
                    {template.tags?.includes('deleted') ? (
                      <Tooltip title={template.is_global && !canEditGlobalTemplate() ? 'Cannot restore global template - Super Admin or Owner role required' : 'Restore template'}>
                        <span>
                          <IconButton
                            onClick={() => handleUnarchive(template.template_id)}
                            color="primary"
                            size="small"
                            disabled={template.is_global && !canEditGlobalTemplate()}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <>
                        {Boolean(template.allow_email_completion) && !isLocationTab && (
                          <Tooltip title="Send via Email">
                            <IconButton
                              onClick={() => openSendEmailDialog(template)}
                              color="info"
                              size="small"
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={!canManageTemplates ? "You do not have permission to edit templates" : (template.is_global && !canEditGlobalTemplate() ? (isLocationTab ? "Edit template" : "Assign asset types to this global template") : "Edit template")}>
                          <span>
                            <IconButton
                              onClick={() => {
                                if (!canManageTemplates) return;
                                handleEditTemplate(template);
                              }}
                              color="primary"
                              size="small"
                              disabled={!canManageTemplates}
                              sx={{ opacity: !canManageTemplates ? 0.5 : 1, cursor: !canManageTemplates ? 'not-allowed' : 'pointer' }}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={!canManageTemplates ? "You do not have permission to copy templates" : "Duplicate template (questions only)"}>
                          <span>
                            <IconButton
                              onClick={() => {
                                if (!canManageTemplates) return;
                                handleDuplicateTemplate(template);
                              }}
                              color="secondary"
                              size="small"
                              disabled={!canManageTemplates}
                              sx={{ opacity: !canManageTemplates ? 0.5 : 1, cursor: !canManageTemplates ? 'not-allowed' : 'pointer' }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          onClick={() => handleArchive(template.template_id)}
                          color="error"
                          size="small"
                          disabled={template.is_global && !canEditGlobalTemplate()}
                          title={template.is_global && !canEditGlobalTemplate() ? "Cannot delete global template - Super Admin or Owner role required" : "Delete template"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Send via Email Dialog */}
      <Dialog
        open={sendEmailDialogOpen}
        onClose={() => setSendEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          Send Check via Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The recipient will receive a link to complete <strong>{sendEmailTemplate?.template_name}</strong> in their browser. No account or app required.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Recipient Email *"
            type="email"
            value={sendEmailRecipientEmail}
            onChange={(e) => setSendEmailRecipientEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Recipient Name (optional)"
            value={sendEmailRecipientName}
            onChange={(e) => setSendEmailRecipientName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Custom message (optional)"
            multiline
            rows={3}
            value={sendEmailCustomMessage}
            onChange={(e) => setSendEmailCustomMessage(e.target.value)}
            placeholder="Add a personal note to include in the email…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailDialogOpen(false)} disabled={sendEmailLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendEmailCheck}
            variant="contained"
            disabled={sendEmailLoading || !sendEmailRecipientEmail.trim()}
            startIcon={sendEmailLoading ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
          >
            {sendEmailLoading ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setCreateDialogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
        PaperProps={{
          sx: { 
            height: isMobile ? '100vh' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <ModernTemplateBuilder
          templateName={templateName}
          setTemplateName={setTemplateName}
          templateDescription={templateDescription}
          setTemplateDescription={setTemplateDescription}
          templateTags={templateTags}
          setTemplateTags={setTemplateTags}
          questions={questions}
          setQuestions={setQuestions}
          selectedAssetTypeIds={selectedAssetTypeIds}
          setSelectedAssetTypeIds={setSelectedAssetTypeIds}
          assetTypes={assetTypes}
          forceConditionImages={forceConditionImages}
          setForceConditionImages={setForceConditionImages}
          minConditionImages={minConditionImages}
          setMinConditionImages={setMinConditionImages}
          allowSkipDvla={allowSkipDvla}
          setAllowSkipDvla={setAllowSkipDvla}
          allowEmailCompletion={allowEmailCompletion}
          setAllowEmailCompletion={setAllowEmailCompletion}
          requiresSection68Waiver={requiresSection68Waiver}
          setRequiresSection68Waiver={setRequiresSection68Waiver}
          section68WaiverText={section68WaiverText}
          setSection68WaiverText={setSection68WaiverText}
          requiresWholeGoodNumber={requiresWholeGoodNumber}
          setRequiresWholeGoodNumber={setRequiresWholeGoodNumber}
          wholeGoodNumberPrefix={wholeGoodNumberPrefix}
          setWholeGoodNumberPrefix={setWholeGoodNumberPrefix}
          suggestedFrequencyEnabled={suggestedFrequencyEnabled}
          setSuggestedFrequencyEnabled={setSuggestedFrequencyEnabled}
          suggestedFrequencyByAssetType={suggestedFrequencyByAssetType}
          setSuggestedFrequencyByAssetType={setSuggestedFrequencyByAssetType}
          onSave={handleCreateTemplate}
          onCancel={() => setCreateDialogOpen(false)}
          isEditing={false}
          businessPermissions={businessPermissions}
          draftAgreements={draftAgreements}
          setDraftAgreements={setDraftAgreements}
          draftSchedules={draftSchedules}
          setDraftSchedules={setDraftSchedules}
          filterTag={filterTag}
        />
      </Dialog>

      {/* OLD Create Template Dialog - KEEPING FOR REFERENCE/BACKUP */}
      <Dialog 
        open={false}
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        style={{ display: 'none' }}
      >
        <DialogTitle>Create New Template (OLD)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Check Type *
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {businessPermissions.allowSafetyChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('safety') || templateTags.length === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['safety']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                        />
                      }
                      label={resolveCheckSingularLabel('nav_safety_checks', adminUiLabels)}
                    />
                  )}
                  {businessPermissions.allowLoanChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('loan')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['loan']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                        />
                      }
                      label={resolveCheckSingularLabel('nav_loan_checks', adminUiLabels)}
                    />
                  )}
                  {businessPermissions.allowDeliveryChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('delivery')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['delivery']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                        />
                      }
                      label={resolveCheckSingularLabel('nav_delivery_checks', adminUiLabels)}
                    />
                  )}
                  {businessPermissions.allowLocationChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('location')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['location']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                        />
                      }
                      label={resolveCheckSingularLabel('nav_location_checks', adminUiLabels)}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select check type (required). Safety is default if none selected.
                </Typography>
              </FormControl>
            </Grid>
            {canCreateGlobal && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isGlobal}
                      onChange={(e) => setIsGlobal(e.target.checked)}
                    />
                  }
                  label="Make this template global (available to all businesses)"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Condition Images Requirement
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Force users to take condition images before completing the check
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceConditionImages}
                      onChange={(e) => {
                        setForceConditionImages(e.target.checked);
                        if (!e.target.checked) {
                          setMinConditionImages(0);
                        }
                      }}
                    />
                  }
                  label="Force Condition Images"
                />
                {forceConditionImages && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Minimum Number of Images"
                      type="number"
                      size="small"
                      fullWidth
                      value={minConditionImages}
                      onChange={(e) => setMinConditionImages(Math.max(0, parseInt(e.target.value) || 0))}
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText="Set the minimum number of condition images required (0 = at least 1 image required)"
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                id="template-tags"
                options={['loan', 'safety', 'maintenance', 'inspection', 'training']}
                value={templateTags}
                onChange={(event, newValue) => {
                  setTemplateTags(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags (e.g., loan, safety, etc.)"
                    helperText="Tags help categorize templates. Type 'loan' to enable loan-specific features."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...chipProps}
                        sx={{
                          borderRadius: 1,
                          m: 0.25,
                          bgcolor: option === 'loan' ? 'warning.50' : 'primary.50',
                          color: option === 'loan' ? 'warning.main' : 'primary.main'
                        }}
                      />
                    );
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  id="asset-types-autocomplete"
                  options={assetTypes}
                  getOptionLabel={(option) => option.type_name}
                  value={assetTypes.filter(type => selectedAssetTypeIds.includes(type.asset_type_id))}
                  onChange={(event, newValue) => {
                    setSelectedAssetTypeIds(newValue.map(item => item.asset_type_id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asset Types"
                      placeholder="Select asset types"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => setCreateAssetTypeDialogOpen(true)}
                                startIcon={<AddIcon />}
                                sx={{ 
                                  ml: 1, 
                                  borderRadius: 1.5,
                                  color: 'primary.main',
                                  borderColor: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.50'
                                  }
                                }}
                                variant="outlined"
                              >
                                New Type
                              </Button>
                            </InputAdornment>
                          </>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    return value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option.type_name}
                          size="small"
                          {...chipProps}
                          sx={{
                            borderRadius: 1,
                            m: 0.25,
                            bgcolor: 'info.50',
                            color: 'info.main'
                          }}
                        />
                      );
                    });
                  }}
                />
              </FormControl>
            </Grid>
            {templateTags.includes('loan') && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Loan Template Features Enabled:</strong><br/>
                    • Damage & Condition Report section<br/>
                    • Terms Agreement with standard loan clauses<br/>
                    • Customer and Business signature fields<br/>
                    These will be automatically added when you create the template.
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Questions
              </Typography>
              {renderQuestionList()}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderRadius: 1 }}>
                <TextField
                  label="Question Text"
                  fullWidth
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Question Type</InputLabel>
                  <Select
                    value={newQuestion.question_type}
                    onChange={(e) => {
                      const newType = e.target.value as QuestionType;
                      // Text headers can never be required
                      const updates: Partial<Question> = { question_type: newType };
                      if (newType === 'text_header') {
                        updates.is_required = false;
                      }
                      setNewQuestion({ ...newQuestion, ...updates });
                    }}
                    label="Question Type"
                  >
                    <MenuItem value="text_header">Text Header (Section Divider)</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                    <MenuItem value="yes_no">Yes/No</MenuItem>
                    <MenuItem value="yes_no_na">Yes/No/N/A</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="image_type">Image (Photo Only)</MenuItem>
                    {!templateTags.includes('loan') && (
                      <MenuItem value="signature">Signature</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {newQuestion.question_type === 'text_header' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Text headers are used to organize and separate sections of your check. 
                    They will be displayed as bold headings in the mobile app.
                  </Alert>
                )}
                {newQuestion.question_type === 'multiple_choice' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                    {(newQuestion.options || []).map((opt, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          label={`Option ${idx + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const updated = [...(newQuestion.options || [])];
                            updated[idx] = e.target.value;
                            setNewQuestion({ ...newQuestion, options: updated });
                          }}
                        />
                        <IconButton
                          onClick={() => {
                            const updated = (newQuestion.options || []).filter((_, i) => i !== idx);
                            setNewQuestion({ ...newQuestion, options: updated });
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setNewQuestion({ ...newQuestion, options: [ ...((newQuestion.options) || []), '' ] })}
                      sx={{ mt: 0.5 }}
                    >
                      Add Option
                    </Button>
                    {((newQuestion.options || []).filter(o => o && o.trim()).length < 2) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Add at least 2 non-empty options.
                      </Typography>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newQuestion.allow_multiple_selections || false}
                          onChange={(e) => {
                            setNewQuestion({ 
                              ...newQuestion, 
                              allow_multiple_selections: e.target.checked,
                              min_selections: e.target.checked ? newQuestion.min_selections : undefined,
                              max_selections: e.target.checked ? newQuestion.max_selections : undefined
                            });
                          }}
                        />
                      }
                      label="Allow Multiple Selections"
                      sx={{ mb: 2, mt: 2 }}
                    />
                    {newQuestion.allow_multiple_selections && (
                      <Box sx={{ mb: 2, pl: 4 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Configure selection limits (leave blank for no limit)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="Minimum Selections"
                            type="number"
                            size="small"
                            value={newQuestion.min_selections || ''}
                            onChange={(e) => setNewQuestion({ 
                              ...newQuestion, 
                              min_selections: e.target.value ? parseInt(e.target.value) : undefined 
                            })}
                            InputProps={{ inputProps: { min: 1 } }}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Maximum Selections"
                            type="number"
                            size="small"
                            value={newQuestion.max_selections || ''}
                            onChange={(e) => setNewQuestion({ 
                              ...newQuestion, 
                              max_selections: e.target.value ? parseInt(e.target.value) : undefined 
                            })}
                            InputProps={{ inputProps: { min: 1 } }}
                            sx={{ flex: 1 }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                {newQuestion.question_type !== 'text_header' && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Required</InputLabel>
                      <Select
                        value={newQuestion.is_required.toString()}
                        onChange={(e) => setNewQuestion({ ...newQuestion, is_required: e.target.value === 'true' })}
                        label="Required"
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newQuestion.image_required || false}
                          onChange={(e) => setNewQuestion({ ...newQuestion, image_required: e.target.checked })}
                        />
                      }
                      label="Require Image"
                      sx={{ mb: 2 }}
                    />
                    {(newQuestion.question_type === 'yes_no' || newQuestion.question_type === 'yes_no_na') && (
                      <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Flexible Image Requirements
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Control when images are required based on user answers
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.force_image_on_yes || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_yes: e.target.checked })}
                            />
                          }
                          label="Force image if user answers Yes"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.force_image_on_no || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_no: e.target.checked })}
                            />
                          }
                          label="Force image if user answers No"
                        />
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                          Return Check Visibility
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Hide this question on return checks based on original loan answers
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.hide_on_return_if_yes || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_yes: e.target.checked })}
                            />
                          }
                          label="Hide on return check if user answered Yes"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.hide_on_return_if_no || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_no: e.target.checked })}
                            />
                          }
                          label="Hide on return check if user answered No"
                        />
                        {newQuestion.question_type === 'yes_no_na' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newQuestion.hide_on_return_if_na || false}
                                onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_na: e.target.checked })}
                              />
                            }
                            label="Hide on return check if user answered N/A"
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Validate multiple choice options
                    if (newQuestion.question_type === 'multiple_choice') {
                      const cleaned = (newQuestion.options || []).map(o => (o || '').trim()).filter(Boolean);
                      if (cleaned.length < 2) {
                        setSnackbar({ open: true, message: 'Please add at least 2 options for multiple choice', severity: 'error' });
                        return;
                      }
                      setNewQuestion({ ...newQuestion, options: cleaned });
                    }
                    handleAddQuestion();
                  }}
                  disabled={!newQuestion.question_text.trim()}
                  fullWidth={isMobile}
                >
                  Add Question
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTemplate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setEditDialogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
        PaperProps={{
          sx: { 
            height: isMobile ? '100vh' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        {isGlobal && !canEditGlobalTemplate() && (
          <Alert severity="info" sx={{ m: 2, mb: 0 }}>
            This is a global template. Only asset type assignments can be changed — template content and questions are managed by the platform.
          </Alert>
        )}
        <ModernTemplateBuilder
          key={selectedTemplate?.template_id}
          templateId={selectedTemplate?.template_id}
          templateName={templateName}
          setTemplateName={setTemplateName}
          templateDescription={templateDescription}
          setTemplateDescription={setTemplateDescription}
          templateTags={templateTags}
          setTemplateTags={setTemplateTags}
          questions={questions}
          setQuestions={setQuestions}
          selectedAssetTypeIds={selectedAssetTypeIds}
          setSelectedAssetTypeIds={setSelectedAssetTypeIds}
          assetTypes={assetTypes}
          forceConditionImages={forceConditionImages}
          setForceConditionImages={setForceConditionImages}
          minConditionImages={minConditionImages}
          setMinConditionImages={setMinConditionImages}
          allowSkipDvla={allowSkipDvla}
          setAllowSkipDvla={setAllowSkipDvla}
          allowEmailCompletion={allowEmailCompletion}
          setAllowEmailCompletion={setAllowEmailCompletion}
          requiresSection68Waiver={requiresSection68Waiver}
          setRequiresSection68Waiver={setRequiresSection68Waiver}
          section68WaiverText={section68WaiverText}
          setSection68WaiverText={setSection68WaiverText}
          requiresWholeGoodNumber={requiresWholeGoodNumber}
          setRequiresWholeGoodNumber={setRequiresWholeGoodNumber}
          wholeGoodNumberPrefix={wholeGoodNumberPrefix}
          setWholeGoodNumberPrefix={setWholeGoodNumberPrefix}
          suggestedFrequencyEnabled={suggestedFrequencyEnabled}
          setSuggestedFrequencyEnabled={setSuggestedFrequencyEnabled}
          suggestedFrequencyByAssetType={suggestedFrequencyByAssetType}
          setSuggestedFrequencyByAssetType={setSuggestedFrequencyByAssetType}
          onSave={handleSaveEditedTemplate}
          onCancel={() => setEditDialogOpen(false)}
          isEditing={true}
          businessPermissions={businessPermissions}
          draftAgreements={draftAgreements}
          setDraftAgreements={setDraftAgreements}
          draftSchedules={draftSchedules}
          setDraftSchedules={setDraftSchedules}
          filterTag={filterTag}
        />
      </Dialog>

      {/* OLD Edit Template Dialog - KEEPING FOR REFERENCE/BACKUP */}
      <Dialog 
        open={false}
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        style={{ display: 'none' }}
      >
        <DialogTitle>Edit Template (OLD)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                required
                disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                multiline
                rows={2}
                disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Check Type *
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {businessPermissions.allowSafetyChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('safety') || templateTags.length === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['safety']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label="Safety Check"
                    />
                  )}
                  {businessPermissions.allowLoanChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('loan')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['loan']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label="Loan Check"
                    />
                  )}
                  {businessPermissions.allowDeliveryChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('delivery')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['delivery']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label="Delivery Check"
                    />
                  )}
                  {businessPermissions.allowLocationChecks && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={templateTags.includes('location')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTemplateTags(['location']);
                            } else {
                              setTemplateTags([]);
                            }
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label={`${locationLabel(businessPermissions.useSiteTerminology)} Check`}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select check type (required). Safety is default if none selected.
                </Typography>
              </FormControl>
            </Grid>
            {canCreateGlobal && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isGlobal}
                      onChange={(e) => setIsGlobal(e.target.checked)}
                      disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                    />
                  }
                  label="Make this template global (available to all businesses)"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Condition Images Requirement
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Force users to take condition images before completing the check
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceConditionImages}
                      onChange={(e) => {
                        setForceConditionImages(e.target.checked);
                        if (!e.target.checked) {
                          setMinConditionImages(0);
                        }
                      }}
                      disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                    />
                  }
                  label="Force Condition Images"
                />
                {forceConditionImages && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Minimum Number of Images"
                      type="number"
                      size="small"
                      fullWidth
                      value={minConditionImages}
                      onChange={(e) => setMinConditionImages(Math.max(0, parseInt(e.target.value) || 0))}
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText="Set the minimum number of condition images required (0 = at least 1 image required)"
                      disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                id="template-tags"
                options={['loan', 'safety', 'maintenance', 'inspection', 'training']}
                value={templateTags}
                onChange={(event, newValue) => {
                  setTemplateTags(newValue);
                }}
                disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags (e.g., loan, safety, etc.)"
                    helperText="Tags help categorize templates. Type 'loan' to enable loan-specific features."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...chipProps}
                        sx={{
                          borderRadius: 1,
                          m: 0.25,
                          bgcolor: option === 'loan' ? 'warning.50' : 'primary.50',
                          color: option === 'loan' ? 'warning.main' : 'primary.main'
                        }}
                      />
                    );
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  id="asset-types-autocomplete"
                  options={assetTypes}
                  getOptionLabel={(option) => option.type_name}
                  value={assetTypes.filter(type => selectedAssetTypeIds.includes(type.asset_type_id))}
                  onChange={(event, newValue) => {
                    setSelectedAssetTypeIds(newValue.map(item => item.asset_type_id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asset Types"
                      placeholder="Select asset types"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => setCreateAssetTypeDialogOpen(true)}
                                startIcon={<AddIcon />}
                                sx={{ 
                                  ml: 1, 
                                  borderRadius: 1.5,
                                  color: 'primary.main',
                                  borderColor: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.50'
                                  }
                                }}
                                variant="outlined"
                              >
                                New Type
                              </Button>
                            </InputAdornment>
                          </>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    return value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option.type_name}
                          size="small"
                          {...chipProps}
                          sx={{
                            borderRadius: 1,
                            m: 0.25,
                            bgcolor: 'info.50',
                            color: 'info.main'
                          }}
                        />
                      );
                    });
                  }}
                />
              </FormControl>
            </Grid>
            {templateTags.includes('loan') && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Loan Template Features Enabled:</strong><br/>
                    • Damage & Condition Report section<br/>
                    • Terms Agreement with standard loan clauses<br/>
                    • Customer and Business signature fields<br/>
                    These will be automatically added when you create the template.
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Questions
              </Typography>
              <Box sx={{ opacity: Boolean(isGlobal) && !canEditGlobalTemplate() ? 0.5 : 1 }}>
                {renderQuestionList()}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderRadius: 1 }}>
                <TextField
                  label="Question Text"
                  fullWidth
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                  sx={{ mb: 2 }}
                  disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Question Type</InputLabel>
                  <Select
                    value={newQuestion.question_type}
                    onChange={(e) => {
                      const newType = e.target.value as QuestionType;
                      // Text headers can never be required
                      const updates: Partial<Question> = { question_type: newType };
                      if (newType === 'text_header') {
                        updates.is_required = false;
                      }
                      setNewQuestion({ ...newQuestion, ...updates });
                    }}
                    label="Question Type"
                    disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                  >
                    <MenuItem value="text_header">Text Header (Section Divider)</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                    <MenuItem value="yes_no">Yes/No</MenuItem>
                    <MenuItem value="yes_no_na">Yes/No/N/A</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="image_type">Image (Photo Only)</MenuItem>
                    {!templateTags.includes('loan') && (
                      <MenuItem value="signature">Signature</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {newQuestion.question_type === 'text_header' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Text headers are used to organize and separate sections of your check. 
                    They will be displayed as bold headings in the mobile app.
                  </Alert>
                )}
                {newQuestion.question_type === 'multiple_choice' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                    {(newQuestion.options || []).map((opt, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          label={`Option ${idx + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const updated = [...(newQuestion.options || [])];
                            updated[idx] = e.target.value;
                            setNewQuestion({ ...newQuestion, options: updated });
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                        <IconButton
                          onClick={() => {
                            const updated = (newQuestion.options || []).filter((_, i) => i !== idx);
                            setNewQuestion({ ...newQuestion, options: updated });
                          }}
                          size="small"
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setNewQuestion({ ...newQuestion, options: [ ...((newQuestion.options) || []), '' ] })}
                      sx={{ mt: 0.5 }}
                      disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                    >
                      Add Option
                    </Button>
                    {((newQuestion.options || []).filter(o => o && o.trim()).length < 2) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Add at least 2 non-empty options.
                      </Typography>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newQuestion.allow_multiple_selections || false}
                          onChange={(e) => {
                            setNewQuestion({ 
                              ...newQuestion, 
                              allow_multiple_selections: e.target.checked,
                              min_selections: e.target.checked ? newQuestion.min_selections : undefined,
                              max_selections: e.target.checked ? newQuestion.max_selections : undefined
                            });
                          }}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label="Allow Multiple Selections"
                      sx={{ mb: 2, mt: 2 }}
                    />
                    {newQuestion.allow_multiple_selections && (
                      <Box sx={{ mb: 2, pl: 4 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Configure selection limits (leave blank for no limit)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="Minimum Selections"
                            type="number"
                            size="small"
                            value={newQuestion.min_selections || ''}
                            onChange={(e) => setNewQuestion({ 
                              ...newQuestion, 
                              min_selections: e.target.value ? parseInt(e.target.value) : undefined 
                            })}
                            InputProps={{ inputProps: { min: 1 } }}
                            sx={{ flex: 1 }}
                            disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                          />
                          <TextField
                            label="Maximum Selections"
                            type="number"
                            size="small"
                            value={newQuestion.max_selections || ''}
                            onChange={(e) => setNewQuestion({ 
                              ...newQuestion, 
                              max_selections: e.target.value ? parseInt(e.target.value) : undefined 
                            })}
                            InputProps={{ inputProps: { min: 1 } }}
                            sx={{ flex: 1 }}
                            disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                {newQuestion.question_type !== 'text_header' && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Required</InputLabel>
                      <Select
                        value={newQuestion.is_required.toString()}
                        onChange={(e) => setNewQuestion({ ...newQuestion, is_required: e.target.value === 'true' })}
                        label="Required"
                        disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newQuestion.image_required || false}
                          onChange={(e) => setNewQuestion({ ...newQuestion, image_required: e.target.checked })}
                          disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                        />
                      }
                      label="Require Image"
                      sx={{ mb: 2 }}
                    />
                    {(newQuestion.question_type === 'yes_no' || newQuestion.question_type === 'yes_no_na') && (
                      <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Flexible Image Requirements
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Control when images are required based on user answers
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.force_image_on_yes || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_yes: e.target.checked })}
                              disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                            />
                          }
                          label="Force image if user answers Yes"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.force_image_on_no || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_no: e.target.checked })}
                              disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                            />
                          }
                          label="Force image if user answers No"
                        />
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                          Return Check Visibility
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Hide this question on return checks based on original loan answers
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.hide_on_return_if_yes || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_yes: e.target.checked })}
                              disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                            />
                          }
                          label="Hide on return check if user answered Yes"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newQuestion.hide_on_return_if_no || false}
                              onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_no: e.target.checked })}
                              disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                            />
                          }
                          label="Hide on return check if user answered No"
                        />
                        {newQuestion.question_type === 'yes_no_na' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newQuestion.hide_on_return_if_na || false}
                                onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_na: e.target.checked })}
                                disabled={Boolean(isGlobal) && !canEditGlobalTemplate()}
                              />
                            }
                            label="Hide on return check if user answered N/A"
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (newQuestion.question_type === 'multiple_choice') {
                      const cleaned = (newQuestion.options || []).map(o => (o || '').trim()).filter(Boolean);
                      if (cleaned.length < 2) {
                        setSnackbar({ open: true, message: 'Please add at least 2 options for multiple choice', severity: 'error' });
                        return;
                      }
                      setNewQuestion({ ...newQuestion, options: cleaned });
                    }
                    handleAddQuestion();
                  }}
                  disabled={!newQuestion.question_text.trim() || (Boolean(isGlobal) && !canEditGlobalTemplate())}
                  fullWidth={isMobile}
                >
                  Add Question
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (isGlobal && !canEditGlobalTemplate()) {
                // Limited update: only assign asset types
                try {
                  const token = localStorage.getItem('token');
                  if (!token || !selectedTemplate) {
                    setError('No authentication token or template selected');
                    return;
                  }
                  await axios.post(
                    `${API_BASE_URL}/check-templates/${selectedTemplate.template_id}/asset-types`,
                    { assetTypeIds: selectedAssetTypeIds },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  await fetchTemplates();
                  setEditDialogOpen(false);
                  resetForm();
                  setSnackbar({ open: true, message: 'Asset types updated', severity: 'success' });
                } catch (err: any) {
                  console.error('Error updating asset types:', err);
                  setError(err?.response?.data?.message || 'Failed to update asset types');
                }
              } else {
                await handleUpdateTemplate();
              }
            }}
            variant="contained"
          >
            {isGlobal && !canEditGlobalTemplate() ? 'Save Asset Types' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog 
        open={editQuestionDialogOpen} 
        onClose={() => setEditQuestionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          {currentEditQuestion && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Question Text"
                fullWidth
                value={currentEditQuestion.question_text}
                onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, question_text: e.target.value })}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={currentEditQuestion.question_type}
                  onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, question_type: e.target.value as QuestionType })}
                  label="Question Type"
                >
                  <MenuItem value="text_header">Text Header (Section Divider)</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                  <MenuItem value="yes_no">Yes/No</MenuItem>
                  <MenuItem value="yes_no_na">Yes/No/N/A</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="image_type">Image (Photo Only)</MenuItem>
                  {!templateTags.includes('loan') && (
                    <MenuItem value="signature">Signature</MenuItem>
                  )}
                </Select>
              </FormControl>
              {currentEditQuestion.question_type === 'text_header' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Text headers are used to organize and separate sections of your check. 
                  They will be displayed as bold headings in the mobile app.
                </Alert>
              )}
              {currentEditQuestion.question_type === 'multiple_choice' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                  {(currentEditQuestion.options || []).map((opt, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        label={`Option ${idx + 1}`}
                        value={opt}
                        onChange={(e) => {
                          const updated = [...(currentEditQuestion.options || [])];
                          updated[idx] = e.target.value;
                          setCurrentEditQuestion({ ...currentEditQuestion, options: updated });
                        }}
                      />
                      <IconButton
                        onClick={() => {
                          const updated = (currentEditQuestion.options || []).filter((_, i) => i !== idx);
                          setCurrentEditQuestion({ ...currentEditQuestion, options: updated });
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCurrentEditQuestion({ ...currentEditQuestion, options: [ ...((currentEditQuestion.options) || []), '' ] })}
                    sx={{ mt: 0.5 }}
                  >
                    Add Option
                  </Button>
                  {((currentEditQuestion.options || []).filter(o => o && o.trim()).length < 2) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Add at least 2 non-empty options.
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentEditQuestion.allow_multiple_selections || false}
                        onChange={(e) => {
                          setCurrentEditQuestion({ 
                            ...currentEditQuestion, 
                            allow_multiple_selections: e.target.checked,
                            min_selections: e.target.checked ? currentEditQuestion.min_selections : undefined,
                            max_selections: e.target.checked ? currentEditQuestion.max_selections : undefined
                          });
                        }}
                      />
                    }
                    label="Allow Multiple Selections"
                    sx={{ mb: 2, mt: 2 }}
                  />
                  {currentEditQuestion.allow_multiple_selections && (
                    <Box sx={{ mb: 2, pl: 4 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Configure selection limits (leave blank for no limit)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Minimum Selections"
                          type="number"
                          size="small"
                          value={currentEditQuestion.min_selections || ''}
                          onChange={(e) => setCurrentEditQuestion({ 
                            ...currentEditQuestion, 
                            min_selections: e.target.value ? parseInt(e.target.value) : undefined 
                          })}
                          InputProps={{ inputProps: { min: 1 } }}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Maximum Selections"
                          type="number"
                          size="small"
                          value={currentEditQuestion.max_selections || ''}
                          onChange={(e) => setCurrentEditQuestion({ 
                            ...currentEditQuestion, 
                            max_selections: e.target.value ? parseInt(e.target.value) : undefined 
                          })}
                          InputProps={{ inputProps: { min: 1 } }}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {currentEditQuestion.question_type !== 'text_header' && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Required</InputLabel>
                    <Select
                      value={currentEditQuestion.is_required ? 'true' : 'false'}
                      onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, is_required: e.target.value === 'true' })}
                      label="Required"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Hide Image Required for yes/no types - they have advanced options instead */}
                  {currentEditQuestion.question_type !== 'yes_no' && currentEditQuestion.question_type !== 'yes_no_na' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={currentEditQuestion.image_required || false}
                          onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, image_required: e.target.checked })}
                        />
                      }
                      label="Require Image"
                      sx={{ mb: 2 }}
                    />
                  )}
                  {(currentEditQuestion.question_type === 'yes_no' || currentEditQuestion.question_type === 'yes_no_na') && (
                    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Flexible Image Requirements
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Control when images are required based on user answers
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentEditQuestion.force_image_on_yes || false}
                            onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, force_image_on_yes: e.target.checked })}
                          />
                        }
                        label="Force image if user answers Yes"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentEditQuestion.force_image_on_no || false}
                            onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, force_image_on_no: e.target.checked })}
                          />
                        }
                        label="Force image if user answers No"
                      />
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Return Check Visibility
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Hide this question on return checks based on original loan answers
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentEditQuestion.hide_on_return_if_yes || false}
                            onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, hide_on_return_if_yes: e.target.checked })}
                          />
                        }
                        label="Hide on return check if user answered Yes"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentEditQuestion.hide_on_return_if_no || false}
                            onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, hide_on_return_if_no: e.target.checked })}
                          />
                        }
                        label="Hide on return check if user answered No"
                      />
                      {currentEditQuestion.question_type === 'yes_no_na' && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={currentEditQuestion.hide_on_return_if_na || false}
                              onChange={(e) => setCurrentEditQuestion({ ...currentEditQuestion, hide_on_return_if_na: e.target.checked })}
                            />
                          }
                          label="Hide on return check if user answered N/A"
                        />
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditQuestionDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            if (currentEditQuestion?.question_type === 'multiple_choice') {
              const cleaned = (currentEditQuestion.options || []).map(o => (o || '').trim()).filter(Boolean);
              if (cleaned.length < 2) {
                setSnackbar({ open: true, message: 'Please add at least 2 options for multiple choice', severity: 'error' });
                return;
              }
              setCurrentEditQuestion({ ...currentEditQuestion, options: cleaned });
            }
            handleSaveEditedQuestion();
          }} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <CreateAssetTypeDialog
        open={createAssetTypeDialogOpen}
        onClose={() => setCreateAssetTypeDialogOpen(false)}
        onCreated={handleAssetTypeCreated}
      />

      {/* ── AI Template Generator ── */}
      <AITemplateGeneratorDialog
        open={aiGeneratorOpen}
        onClose={() => setAiGeneratorOpen(false)}
        filterTag={filterTag}
        onApplyTemplate={handleApplyAiTemplate}
      />

      {/* ── Import JSON Dialog ── */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <UploadFileIcon color="primary" />
          Import Template from JSON
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>

          {/* ── Step: input ── */}
          {importStep === 'input' && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Schema reference */}
              <Alert severity="info" icon={false} sx={{ fontFamily: lato, fontSize: '0.82rem' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>JSON schema</Typography>
                <Box component="pre" sx={{ m: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{`{
  "template_name": "string (required)",
  "description": "string",
  "tags": ["safety" | "loan" | "delivery" | "location"],
  "is_global": false,
  "force_condition_images": false,
  "min_condition_images": 0,
  "allow_email_completion": false,
  "allow_skip_dvla": false,
  "requires_section_6_8_waiver": false,
  "section_6_8_waiver_text": "string | null",
  "requires_whole_good_number": false,
  "whole_good_number_prefix": "WGS",
  "questions": [
    {
      "question_text": "string (required)",
      "question_type": "yes_no_na | yes_no | text | multiple_choice | number | date | signature | text_header | email_check | image_type",
      "is_required": true,
      "image_required": false,
      "force_image_on_yes": false,
      "force_image_on_no": false,
      "hide_on_return_if_yes": false,
      "hide_on_return_if_no": false,
      "hide_on_return_if_na": false,
      "options": ["Option A", "Option B"],
      "allow_multiple_selections": false,
      "min_selections": null,
      "max_selections": null,
      "show_conditions": null
    }
  ]
}`}</Box>
              </Alert>

              {/* File upload */}
              <Box>
                <Typography variant="body2" sx={{ fontFamily: lato, mb: 0.75, fontWeight: 600 }}>
                  Upload a .json file
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  size="small"
                  sx={primaryButtonSx}
                >
                  Choose File
                  <input type="file" accept=".json,application/json" hidden onChange={handleImportFileUpload} />
                </Button>
              </Box>

              <Typography variant="body2" sx={{ fontFamily: lato, color: 'text.secondary', textAlign: 'center' }}>
                — or paste JSON directly —
              </Typography>

              {/* Text area */}
              <TextField
                multiline
                rows={14}
                fullWidth
                placeholder='{ "template_name": "My Template", "questions": [ ... ] }'
                value={importJsonText}
                onChange={(e) => {
                  setImportJsonText(e.target.value);
                  setImportErrors([]);
                  setImportWarnings([]);
                }}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                variant="outlined"
              />

              {/* Validation feedback */}
              {importErrors.length > 0 && (
                <Alert severity="error">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Please fix the following:</Typography>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </Alert>
              )}
              {importWarnings.length > 0 && importErrors.length === 0 && (
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Warnings (import will still proceed):</Typography>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </Alert>
              )}
            </Box>
          )}

          {/* ── Step: preview ── */}
          {importStep === 'preview' && importParsed && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success" sx={{ fontFamily: lato }}>
                JSON is valid. Review the details below before importing.
              </Alert>

              {importWarnings.length > 0 && (
                <Alert severity="warning">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily: lato }}>
                  Template details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: lato }}>
                    <strong>Name:</strong> {importParsed.template_name}
                  </Typography>
                  {importParsed.description && (
                    <Typography variant="body2" sx={{ fontFamily: lato }}>
                      <strong>Description:</strong> {importParsed.description}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontFamily: lato }}>
                    <strong>Tags:</strong>{' '}
                    {(Array.isArray(importParsed.tags) && importParsed.tags.length > 0)
                      ? importParsed.tags.join(', ')
                      : `none (will default to ${filterTag || 'safety'})`}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: lato }}>
                    <strong>Questions:</strong> {(importParsed.questions || []).length}
                  </Typography>
                  {importParsed.force_condition_images && (
                    <Typography variant="body2" sx={{ fontFamily: lato }}>
                      <strong>Force condition images:</strong> yes (min {importParsed.min_condition_images ?? 0})
                    </Typography>
                  )}
                  {importParsed.allow_email_completion && (
                    <Typography variant="body2" sx={{ fontFamily: lato }}>
                      <strong>Allow email completion:</strong> yes
                    </Typography>
                  )}
                  {importParsed.requires_section_6_8_waiver && (
                    <Typography variant="body2" sx={{ fontFamily: lato }}>
                      <strong>Section 6/8 waiver:</strong> yes
                    </Typography>
                  )}
                  {importParsed.requires_whole_good_number && (
                    <Typography variant="body2" sx={{ fontFamily: lato }}>
                      <strong>Wholegood number:</strong> {importParsed.whole_good_number_prefix || 'WGS'} required
                    </Typography>
                  )}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily: lato }}>
                  Questions ({(importParsed.questions || []).length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(importParsed.questions || []).map((q: any, i: number) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 22, fontFamily: lato, pt: 0.1 }}>
                        {i + 1}.
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: lato }}>
                          {q.question_text}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                          <Chip size="small" label={q.question_type} variant="outlined" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          {(q.is_required === true || q.required === 1) && (
                            <Chip size="small" label="Required" color="primary" variant="outlined" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          )}
                          {(q.image_required === true || q.image_required === 1) && (
                            <Chip size="small" label="Image Required" color="warning" variant="outlined" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          )}
                          {q.question_type === 'multiple_choice' && Array.isArray(q.options) && (
                            <Chip size="small" label={`${q.options.length} options`} variant="outlined" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          )}
                          {q.allow_multiple_selections && (
                            <Chip size="small" label="Multi-select" color="info" variant="filled" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          )}
                          {q.show_conditions && (
                            <Chip size="small" label="Has conditions" color="secondary" variant="outlined" sx={{ fontFamily: lato, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {importErrors.length > 0 && (
                <Alert severity="error">
                  {importErrors.map((e, i) => <div key={i}>{e}</div>)}
                </Alert>
              )}
            </Box>
          )}

          {/* ── Step: done ── */}
          {importStep === 'done' && (
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontFamily: lato, fontWeight: 700 }}>
                Template imported successfully!
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: lato, color: 'text.secondary', textAlign: 'center' }}>
                Your new template is now active and ready to use.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {importStep === 'input' && (
            <>
              <Button onClick={() => setImportDialogOpen(false)} sx={primaryButtonSx}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleImportValidate}
                disabled={!importJsonText.trim()}
                sx={primaryButtonSx}
              >
                Validate &amp; Preview
              </Button>
            </>
          )}
          {importStep === 'preview' && (
            <>
              <Button onClick={() => { setImportStep('input'); setImportErrors([]); }} sx={primaryButtonSx}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleImportSubmit}
                disabled={importLoading}
                startIcon={importLoading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                sx={primaryButtonSx}
              >
                {importLoading ? 'Importing…' : 'Import Template'}
              </Button>
            </>
          )}
          {importStep === 'done' && (
            <Button
              variant="contained"
              onClick={() => setImportDialogOpen(false)}
              sx={primaryButtonSx}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* ── End Import JSON Dialog ── */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 12000 : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%', maxWidth: 480 }}
        >
          <strong>{snackbar.message}</strong>
          {snackbar.detail && (
            <div style={{ marginTop: 4, fontSize: '0.85em', opacity: 0.9 }}>
              {snackbar.detail}
            </div>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckTemplateManagement; 